import type { Dict } from './de';

const en: Dict = {
  app: {
    name: 'Projektzeit',
    demo: 'Demo',
    demoNotice: 'Demo with fictional data. Changes last until the page is reloaded.',
    loading: 'Loading …',
    loadingDemo: {
      db: 'Starting database …',
      schema: 'Creating tables …',
      data: 'Generating sample data …',
    },
    noWasm: 'The demo could not start because this view does not allow WebAssembly. Run it locally: npm install, then npm run dev:demo -w @pz/web.',
    fatal: 'The application could not be started.',
  },
  nav: {
    myAbsences: 'My absences',
    team: 'Team',
    planning: 'Planning',
    evaluation: 'Staff review',
    absences: 'Absences',
    rejectedBadge: '{{count}} rejected entries',
    rejectedBadge_one: '{{count}} rejected entry',
    overview: 'Overview',
    week: 'My week',
    entries: 'My hours',
    tasks: 'Tasks',
    approvals: 'Time approvals',
    billing: 'Billing',
    workload: 'Workload',
    projects: 'Projects',
    people: 'People',
    admin: 'Administration',
    logout: 'Sign out',
    theme: 'Appearance',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    menu: 'Menu',
    groupMine: 'My area',
    groupProjects: 'Projects and team',
    groupLead: 'Management',
    groupAdmin: 'Administration',
    cockpit: 'Cockpit',
    help: 'Quick guide',
    leaveRequests: 'Absence requests',
    analysis: 'Project analysis',
    customers: 'Customers',
    planningGroup: 'Planning & capacity',
    insightsGroup: 'Reports',
    subTime: 'Hours',
    approvalsGroup: 'Approvals',
    peopleGroup: 'People',
    announcements: 'Announcements',
  },
  common: {
    moveDialog: 'Drag to move; double click: back to its place',
    day: 'Day',
    undo: 'Undo',
    undone: 'Undone',
    undoReason: 'Change undone',
    sortAsc: 'Sort ascending (your choice is kept)',
    sortDesc: 'Sort descending',
    sortReset: 'Back to the standard order',
    shiftHint: 'Hold Shift and click to select a whole range.',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    search: 'Search',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    reset: 'Reset',
    apply: 'Apply',
    from: 'From',
    to: 'To',
    date: 'Date',
    week: 'Week',
    month: 'Month',
    year: 'Year',
    hours: 'Hours',
    total: 'Total',
    status: 'Status',
    note: 'Note',
    reason: 'Reason',
    reasonHint: 'Stored in the change log.',
    optional: 'optional',
    exportExcel: 'Export to Excel',
    today: 'Today',
    previous: 'Previous',
    next: 'Next',
    open: 'Open',
    archive: 'Archive',
    unarchive: 'Restore',
    archived: 'Archived',
    active: 'Active',
    inactive: 'Inactive',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    project: 'Project',
    task: 'Task',
    person: 'Person',
    description: 'Description',
    booked: 'Booked',
    planned: 'Planned',
    budget: 'Budget',
    of: 'of',
    h: 'h',
    cw: 'CW',
    noData: 'No data in the selected period.',
    saved: 'Saved',
    deleted: 'Deleted',
    copied: 'Copied',
    copy: 'Copy',
    history: 'History',
    createdAt: 'Recorded on',
    select: 'Select',
    selectAll: 'Select all',
    selected: '{{count}} selected',
    period: 'Period',
    type: 'Type',
    showArchived: 'Show archived',
    showInactive: 'Show inactive',
    noResults: 'No results.',
    typeToSearch: 'Type to search',
    noMatches: 'No matches',
    moreMatches: '{{count}} more matches: please refine the search',
    demoPrint: 'In the demo the print window may be blocked. In the installed platform the print dialog opens directly; choose "Save as PDF" there.',
  },
  status: {
    draft: 'Draft',
    submitted: 'Submitted',
    approved_l1: 'Lead approved',
    approved: 'Approved',
    rejected: 'Rejected',
    billed: 'Billed',
  },
  billingType: {
    billable: 'Billable',
    included: 'Included in fixed fee',
    non_billable: 'Do not bill',
    billableHint: 'Appears on the timesheet and the invoice.',
    includedHint: 'Covered by the fixed fee; shown on the timesheet, not as an amount.',
    non_billableHint: 'Internal, training, business development: not part of billing.',
    fixed: 'Set by the task',
  },
  task: {
    manual: 'Manual',
    internal: 'Internal',
    deleted: 'Task deleted',
    deletedHint: 'The task was deleted. Please move the draft to another task or delete it; it is not submitted like this.',
    formaDeleted: 'Deleted in Forma',
    formaAccessLost: 'No longer visible in Forma',
    closed: 'Closed',
    done: 'Done',
    openInForma: 'Open in Forma',
    status: {
      draft: 'Draft',
      open: 'Open',
      pending: 'Pending',
      in_progress: 'In progress',
      in_review: 'In review',
      completed: 'Completed',
      not_approved: 'Not approved',
      in_dispute: 'In dispute',
      closed: 'Closed',
    },
  },
  week: {
    withdrawWeek: 'Withdraw week',
    markAbsence: 'Add absence',
    title: 'My week',
    titleOther: 'Week of {{name}}',
    submitWeek: 'Submit week',
    submitDone: '{{count}} entries submitted',
    submitDone_one: '{{count}} entry submitted',
    nothingToSubmit: 'No drafts in this week.',
    addEntry: 'Entry',
    recent: 'Recently used',
    recentEmpty: 'No bookings in the last weeks.',
    absence: {
      special: 'Special leave',
      overtime: 'Time off in lieu',
      unpaid: 'Unpaid leave',
      child_sick: 'Child sick',
      parental: 'Parental / maternity leave',
      pending: 'requested: {{kind}}',
      vacation: 'Vacation',
      sick: 'Sick',
      training: 'Training',
      other: 'Other absence',
      absent: 'Absent',
    },
    locked: 'Month closed',
    noEntries: 'No entries',
    viewPerson: 'View week of',
    me: 'Me',
    lockedHint: 'This month is closed. Changes only after the office reopens it.',
    rejectedHint: 'Rejected: {{reason}}',
    tipKeyboard: 'Tip: enter hours as 7.5 or 7:30.',
    showWeekend: 'Weekend',
    showWeekendHint: 'Show Saturday and Sunday. The choice is kept in this browser.',
    weekendNeeded: 'Shown because there are weekend bookings, the weekend has working days by contract or today is a weekend day.',
    compHint: 'incl. {{hours}} h time off in lieu',
    freeDay: 'day off',
    copied: 'Copy created on {{date}}',
    moved: 'Moved to {{date}}',
    moveOrCopy: 'Move or copy',
    copyHere: 'Copy',
    moveHere: 'Move',
    targetDate: 'Target day',
    dragHint: 'Tip: entries can also be dragged to another day; holding Ctrl or Alt copies them.',
    dragEntryHint: 'Drag onto a day: move · with Ctrl or Alt: copy',
    dragTaskHint: 'Drag onto a day: new entry with this task · between the favorites: put it there',
    dropNew: 'New entry on {{day}}',
    dropMove: 'Move here',
    dropCopy: 'Copy here',
    dayFull: 'Daily target reached',
    dayOver: 'More than the daily target',
    tipDrag: 'Tip: drag a task onto a day to book there; enter hours as 7.5 or 7:30.',
  },
  entry: {
    submittedDone: '{{count}} entries submitted',
    submittedDone_one: '{{count}} entry submitted',
    withdrawnDone: '{{count}} entries withdrawn',
    withdrawnDone_one: '{{count}} entry withdrawn',
    restored: 'Restored',
    billingTypeByRule: 'Taken from project or task',
    newTitle: 'Record time',
    editTitle: 'Edit entry',
    task: 'Task',
    pickTask: 'Search task: number or title',
    changeTask: 'Other task',
    hours: 'Hours',
    hoursHint: 'e.g. 7.5 or 7:30 (step {{step}} min)',
    description: 'Activity',
    descriptionHint: 'Short and clear, in your language if you like; shown on the timesheet, where the office adjusts the day text for the client if needed.',
    billingType: 'Billing type',
    location: 'Work location',
    date: 'Date',
    billedMinutes: 'Hours to bill',
    billedMinutesHint: 'Empty = as recorded. May be higher or lower.',
    billingNote: 'Billing note',
    saveAndNew: 'Save and next',
    bookFor: 'Book for',
    deleteTitle: 'Delete entry',
    deleteText: 'The entry moves to the trash and can be restored.',
    submit: 'Submit',
    withdraw: 'Withdraw',
    restore: 'Restore',
    warnings: {
      TASK_FORMA_DELETED: 'The task was deleted in Forma. The booking is saved anyway.',
      TASK_FORMA_ACCESS_LOST: 'The task is no longer visible in Forma. The booking is saved anyway.',
      TASK_CLOSED: 'The task is closed.',
      DAILY_LIMIT: 'More than {{hours}} hours on this day.',
      WEEKEND: 'Booking on a weekend. The hours count as extra hours (target that day: 0) and can be taken as time off in lieu.',
      WEEKEND_DATE: 'Booking on a weekend. Please check the date.',
      FREE_DAY: 'Booking on a day off by contract. The hours count as extra hours.',
      HOLIDAY: 'Booking on a public holiday or office day off. The hours count as extra hours.',
      ABSENCE_DAY: 'A full-day absence is entered for this day. If you worked after all, withdraw the absence or ask HR to adjust it.',
    },
    audit: {
      'time.submit': 'Submitted',
      'time.withdraw': 'Withdrawn',
      'time.approve_undo': 'Approval undone',
      'time.reject_undo': 'Rejection undone',
      'time.update': 'Changed',
      'time.delete': 'Deleted',
      'time.restore': 'Restored',
      'time.approve': 'Approved',
      'time.reject': 'Rejected',
      'time.reopen': 'Reopened',
      'time.create_for_other': 'Recorded for another person',
      'time.billed': 'Hours to invoice changed',
    },
    locationNone: 'not specified',
    billedTitle: 'Hours to invoice',
    recorded: 'recorded {{hours}} h',
    billedReset: 'As recorded',
    billedInfo: 'Applies to the client invoice only. Key figures, workload and the plan/actual ratio use the recorded hours.',
    billedShort: 'Inv. {{hours}} h',
    billedAction: 'Inv. hrs',
    noOvertime: 'Does not count as extra hours',
    noOvertimeHint: 'For example voluntary training outside working hours. The hours stay booked.',
    noOvertimeByCategory: 'The category of the task does not count as extra hours.',
  },
  entries: {
    nothingFor: 'None of the selected entries allows this action.',
    deletedMany: '{{count}} entries moved to the trash',
    deletedMany_one: '{{count}} entry moved to the trash',
    title: 'My hours',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    trash: 'Trash',
    count: '{{count}} entries',
    count_one: '{{count}} entry',
    sum: 'Total',
    exportHint: 'Excel with all entries of the period including your description, totals per day and per project. Also usable as your own backup.',
    thisWeek: 'This week',
    showing: '{{shown}} of {{total}} entries shown, more while scrolling',
    capped: 'The newest {{count}} entries of the period are shown. Choose a shorter period; the Excel export contains every entry.',
    formaLostFilter: 'Filtered: entries on Forma tasks that were deleted or can no longer be reached. Please rebook them or clarify with the office.',
    taskFilter: 'Filtered: entries on one task.',
    clearFilter: 'Clear filter',
  },
  tasks: {
    showHidden: 'Show hidden',
    hideSelected: 'Hide',
    showSelected: 'Show again',
    hiddenMany: '{{count}} tasks hidden',
    hiddenMany_one: '{{count}} task hidden',
    shownMany: '{{count}} tasks shown again',
    shownMany_one: '{{count}} task shown again',
    title: 'Tasks',
    search: 'Number, title or project',
    includeClosed: 'Show closed',
    createManual: 'Manual task',
    manualTitle: 'Create manual task',
    manualHint: 'For work without a Forma issue, e.g. training, business development, meetings. Anything that exists in Forma belongs there as an issue.',
    category: 'Category',
    similar: 'Similar tasks already exist:',
    billingTypeFixed: 'Fix the billing type for all bookings',
    created: 'Task {{ref}} created',
    book: 'Book time',
    count: '{{count}} tasks',
    count_one: '{{count}} task',
    empty: 'No bookable tasks found. Project leads assign people to projects.',
    subtitle: 'All tasks I can book on: Forma issues and manual tasks (M numbers).',
    billingForced: 'Set by the category or the internal project.',
    planHint: 'Optional. Basis for plan/actual per task.',
    formaKind: 'Category / type (Forma)',
    allKinds: 'All categories and types',
    wholeCategory: '{{category}}: all types',
    typeExcluded: '{{type}} (not taken over)',
    withoutValue: '(no value)',
    period: 'Start - due',
    allStatuses: 'All statuses',
    resetFilters: 'Reset filters',
    selectAllShown: 'Select all {{count}}',
    noneFiltered: 'No tasks for these filters.',
    none: 'No tasks yet. Forma issues appear after the sync; manual tasks via "Create task".',
    kindColumn: 'Category / type',
    hiddenByFilter: 'Hidden by the import filter (type is not taken over)',
    filteredOut: 'Import filter',
    hide: 'Hide',
    listLegend: 'Plan: planned hours from Projektzeit (0 = not evaluated). Hidden tasks keep their hours and can be shown again. Forma issues are read only.',
    clashHint: 'To keep clash issues out for good:',
    toImportFilter: 'import filter in the "Forma" tab',
    editManual: 'Edit task {{ref}}',
    number: 'Number',
    numberHint: 'Unique in the project',
    numberTaken: '{{ref}} exists in this project already (hidden tasks count too). Two manual tasks cannot share a number.',
    numberInvalid: 'Please enter a number from 1 to 9999999.',
    numberChangeHint: 'The new number applies everywhere: in hours, approvals, analyses, favorites and open billing drafts (there with the hint "data changed"). Finalized documents keep the number of that time.',
    renameBooked: 'The new name applies everywhere, also to the {{hours}} h booked already. If it is different work, better create a new task.',
    renameHint: 'The new name applies wherever the task appears.',
    delete: {
      button: 'Delete …',
      title: 'Delete {{ref}}?',
      hideInstead: 'Hide instead',
      confirm: 'Delete for good',
      hidden: '{{ref}} hidden; the hours are kept',
      done: '{{ref}} deleted, {{count}} entries removed',
      done_one: '{{ref}} deleted, {{count}} entry removed',
      warnTitle: 'Deleting the task deletes the hours booked on it.',
      warnHours: 'Affected: {{count}} entries of {{people}} people, {{hours}} h in all.',
      warnHours_one: 'Affected: {{count}} entry, {{hours}} h.',
      warnNoHours: 'Nothing (visible) is booked on the task yet.',
      warnFinal: 'The deletion cannot be undone. That is why it is almost always better not to delete the task.',
      drafts: 'Drafts that people have not submitted yet are private: they stay with the people, who move them to another task.',
      better: 'Better: "Hide instead". The task disappears from booking, all hours are kept, and it can be shown again at any time.',
      blocked: 'Deleting is not possible: {{count}} entries are in a closed month or invoiced. The task can only be hidden.',
      blocked_one: 'Deleting is not possible: {{count}} entry is in a closed month or invoiced. The task can only be hidden.',
      approvedNeedsRight: '{{count}} entries are approved already; only someone who may correct entries (office) deletes them.',
      understood: 'I understand that the hours are deleted and that this cannot be undone.',
    },
    startDate: 'Start',
    dueDate: 'Due',
    datesInvalid: 'Due is before the start.',
    datesHint: 'With start and due date the task appears in the schedule of the project analysis and in the schedule state.',
    closedLabel: 'Finished (closed)',
    closedHint: 'Closed tasks count as finished in the progress; booking stays possible.',
    kindCount: '{{kind}} ({{count}})',
    billingColumn: 'Billing',
    sortNumber: 'No.',
    truncated: 'More than {{count}} tasks match the filter; the first {{count}} are shown. Please narrow the search or filter.',
    formaDraft: 'Draft in Forma',
    formaDraftHint: 'Still a draft in Forma; appears as soon as it is released or drafts are taken over.',
    billingByRole: 'The billing type is set by the project lead, billing and the office; otherwise the default of the category applies.',
    billingMany: 'Set billing type …',
    billingSetMany: 'Billing type set for {{count}} manual tasks',
    billingSkipped: '{{count}} tasks skipped (Forma task or fixed by the category)',
  },
  approvals: {
    weekendHint: 'Booked on a weekend',
    undone: '{{count}} entries reset',
    undone_one: '{{count}} entry reset',
    undoSkipped: '{{count}} entries were processed further in the meantime and stay unchanged.',
    undoSkipped_one: '{{count}} entry was processed further in the meantime and stays unchanged.',
    title: 'Approvals',
    subtitle: 'Check submitted hours. Each project decides who approves: the project lead, the office, or first the project lead (stage 1), then the office (stage 2).',
    mine: 'My approvals',
    all: 'All open',
    stage: {
      l1: 'Approval by project lead',
      final: 'Approval by office',
      override: 'Approve directly',
    },
    approve: 'Approve',
    reject: 'Reject',
    rejectTitle: 'Reject entries',
    rejectReason: 'What needs to be corrected?',
    done: '{{count}} entries approved',
    done_one: '{{count}} entry approved',
    rejected: '{{count}} entries rejected',
    rejected_one: '{{count}} entry rejected',
    skipped: '{{count}} skipped',
    nothing: 'Nothing to check.',
    weekOf: 'CW {{week}}',
    bulk: {
      open: 'Hours: export / import',
      title: 'Edit open hours with a file',
      intro: '1. Export the open entries as CSV. 2. In Excel change only the columns "hours", "description" and "billing_type". 3. Import the file: preview first, then apply. Changes follow the same rules as the edit dialog (rights, version, closed months) and are logged; the entries stay open for approval.',
      export: '1. Export (CSV)',
      exported: '{{count}} entries exported',
      save: 'Save file',
      copy: 'Copy text',
      fileText: 'File content (editable)',
      import: '2. Import',
      chooseFile: 'Choose CSV file',
      orPaste: 'or paste the content here',
      preview: 'Preview',
      apply: 'Apply changes',
      summary: '{{changed}} changed, {{unchanged}} unchanged, {{errors}} with errors',
      previewOnly: 'Preview: nothing has been changed yet.',
      applied: '{{count}} entries changed',
      line: 'Line',
      person: 'Person',
      date: 'Date',
      change: 'Change',
      state: 'Result',
      stateChanged: 'will change',
      stateApplied: 'changed',
      stateUnchanged: 'unchanged',
      stateError: 'Error',
      fields: {
        hours: 'Hours',
        description: 'Description',
        billing_type: 'Billing type',
      },
      errors: {
        NOT_IN_QUEUE: 'Not (or no longer) one of your open approvals',
        CONFLICT: 'Changed since the export (version)',
        BAD_HOURS: 'Invalid hours (mind the step and the maximum)',
        BAD_BILLING_TYPE: 'Invalid billing type (billable, included, non_billable)',
        PERIOD_LOCKED: 'Month is closed',
        ENTRY_BILLED: 'Already billed',
        ENTRY_DELETED: 'Entry was deleted',
        ENTRY_APPROVED: 'Already approved',
        SELF_APPROVAL: 'You cannot edit your own entries here',
        WRONG_STATUS: 'Status does not fit (no longer open for approval)',
        FORBIDDEN: 'No permission to edit',
        NOT_FOUND: 'Entry not found',
        REASON_REQUIRED: 'Reason required',
        OTHER: 'Not applied',
      },
      noRows: 'No open entries to export.',
    },
    groupsShown: '{{shown}} of {{total}} person-weeks shown, more while scrolling',
    filterPerson: 'Person',
    allPersons: 'All people',
    filterKind: 'Kind of task',
    allKinds: 'All tasks',
    kindForma: 'Forma tasks',
    kindManual: 'Manual tasks (no category)',
    summaryLine: '{{shown}} of {{total}} entries · {{hours}} h open',
    moreHint: 'Shown: {{shown}} of {{total}} open entries. Filters by person or task make the list smaller.',
    loadMore: 'Load more',
  },
  billing: {
    print: 'Print / PDF',
    printHint: 'Print layout with signature fields; choose "Save as PDF" in the print dialog.',
    noAccess: 'Billing is reserved for people with the "Billing" right (e.g. the management assistant). Project leads approve hours.',
    title: 'Billing',
    subtitle: 'Timesheets and progress invoices',
    documents: 'Documents',
    search: 'Search number, title or project',
    listCapped: 'The newest {{count}} documents are shown. Search by number, title or project to find older ones.',
    empty: 'No documents yet. Create a timesheet or a progress invoice above.',
    noMatch: 'No documents for this search. Change the search term or the status.',
    unbilledNone: 'Everything approved has been billed.',
    newSn: 'Timesheet',
    newAbschlag: 'Progress invoice',
    newSnTitle: 'Create timesheet',
    newAbschlagTitle: 'Create progress invoice',
    unbilled: 'Approved, not billed yet',
    unbilledHint: 'Billable hours with status approved that are not on a finalized timesheet.',
    noRate: 'no hourly rate',
    kinds: {
      stundennachweis: 'Timesheet',
      abschlag: 'Progress invoice',
    },
    docStatus: {
      draft: 'Draft',
      final: 'Finalized',
      invoiced: 'Invoiced',
      paid: 'Paid',
      cancelled: 'Cancelled',
    },
    number: 'Number',
    header: 'Header data',
    options: 'Options',
    layout: {
      label: 'Layout',
      matrix: 'Days × people',
      list: 'List',
    },
    includeTypes: 'Included billing types',
    showAmounts: 'Show amounts (hourly rates)',
    language: 'Document language',
    dayTextPrefix: {
      label: 'Day text starts with',
      discipline: 'Discipline',
      name: 'Name',
      none: 'nothing',
      code: 'Code of the person (HA, HAL)',
    },
    includeUnapproved: 'Show unapproved entries in the preview',
    preview: 'Preview',
    dayText: 'Description of work',
    dayTextReset: 'Use automatic text',
    dayTextEdited: 'edited',
    rows: 'Single bookings',
    exclude: 'Do not bill',
    billedHours: 'Billed h',
    finalize: 'Finalize',
    finalizeTitle: 'Finalize document',
    finalizeText: 'A number is assigned, the content is frozen and the bookings are marked as billed. Only approved bookings are included.',
    finalized: 'Finalized as {{number}}',
    markInvoiced: 'Invoiced',
    markPaid: 'Paid',
    cancel: 'Cancel document',
    cancelTitle: 'Cancel document',
    cancelText: 'The bookings are released and can be billed again. The number stays as cancelled.',
    deleteDraft: 'Delete draft',
    fields: {
      customerReference: 'Client reference / order number',
      title: 'Title',
      consultant: 'Contractor',
      customer: 'Client',
      projectLabel: 'Project name',
      projectNumber: 'Project number',
      partner: 'Partner',
      periodLabel: 'Period (text)',
      siteLocation: 'Site',
      place: 'Place',
      documentDate: 'Date',
      providerSignatureName: 'Name under the signature (contractor)',
      clientSignatureName: 'Name under the signature (client)',
      issuerName: 'Company (header line)',
      issuerAddress: 'Address (header line)',
      note: 'Note in the document',
      contractNote: 'Contract basis',
      asOf: 'Progress as of',
      sequenceLabel: 'Sequence',
      vatPercent: 'VAT %',
      customerAddress: 'Client address',
      customerContact: 'Client contact person',
    },
    phases: 'Work stages',
    phase: 'Work stage',
    weight: 'Weight %',
    fee: 'Fee',
    progress: 'Progress %',
    earned: 'Earned',
    previous: 'Previously invoiced',
    current: 'This invoice',
    extras: 'Additional items',
    addExtra: 'Add item',
    net: 'Net',
    vat: 'VAT',
    gross: 'Gross',
    previousDocs: 'Previous progress invoices',
    unapprovedWarning: '{{count}} bookings in the period are not approved yet and will not be billed.',
    unapprovedWarning_one: '{{count}} booking in the period is not approved yet and will not be billed.',
    noEntries: 'No approved bookings in the period.',
    amount: 'Amount',
    readOnly: 'Finalized documents cannot be changed. Cancel and create a new one to correct.',
    recalcHint: 'Amounts are recalculated after saving.',
    noFee: 'No fee is stored for this project (project > fee).',
    billedHint: 'Hours to invoice start with the recorded hours and may differ, also upwards (e.g. lump-sum agreement). All other analyses use the recorded hours.',
    printPreview: 'Print preview',
    dayTextTask: {
      label: 'Task in the day text',
      fallback: 'only for bookings without description',
      always: 'always, as [task] before the sentences',
    },
    dayTextHint: 'Per day one line per discipline (or person). Every description becomes a sentence; equal sentences appear once; without a description the task title stands, unless the line already describes that task. Chosen work locations are added in brackets. Texts edited by hand are kept and flagged when the bookings of the day change afterwards.',
    dayTextOutdated: 'Bookings changed after the edit',
    dayTextOutdatedHint: 'Automatic text for the current bookings:',
    dayTextKeep: 'Keep edited text',
    outdatedNotice: '{{count}} day texts edited by hand may no longer match the bookings (flagged in the preview). Please check them.',
    outdatedNotice_one: 'One day text edited by hand may no longer match the bookings (flagged in the preview). Please check it.',
    finalizeOutdated: 'Note: {{count}} edited day texts were not checked after the bookings changed.',
    finalizeOutdated_one: 'Note: one edited day text was not checked after the bookings changed.',
    dayTextLocations: 'Name the work location in the day text',
    payment: {
      title: 'Payment terms',
      intro: 'Payment term, discount for early payment and deductions after VAT for this invoice. Pre-filled from the terms of the project (Customers › Terms); this document may differ from them. The invoice in the accounting system remains the original.',
      none: 'No payment terms are stored for this project yet. Enter them here for this document, or permanently in the terms of the project.',
      fromProject: 'Take over from the project terms',
      summary: 'What the customer pays',
      frozen: 'Fixed with the document: later changes to the project terms do not change it.',
      noteSaved: 'Amounts and due date use the saved state of the positions.',
    },
    basis: {
      changedShort: 'Data changed',
      changed: 'System data changed since this draft was prepared: {{parts}}. Please check; the document does not change by itself.',
      checked: 'Marked as checked',
      markChecked: 'Mark as checked',
      takeRecipient: 'Take recipient from customer data',
      takeProject: 'Take project number and name',
      parts: {
        entries: 'time entries of the period',
        texts: 'Names of people or tasks',
        rates: 'Sales rates (amounts)',
        customer: 'customer data (recipient)',
        project: 'project number or name',
        fee: 'fee, work stages or terms',
      },
    },
    edit: {
      export: 'For editing (Excel)',
      exportHint: 'Draft as an Excel file for editing: change the yellow columns and load it back here',
      import: 'Load edits',
      previewTitle: 'Edited file: preview',
      apply: 'Apply {{count}} changes',
      applied: '{{count}} changes applied',
      previewRefreshed: 'The system data changed since the preview. The preview now shows the new state; please check it and apply again.',
      stale: 'The draft was changed after the export. The preview compares with the current state; please check the changes.',
      noChanges: 'The file contains no changes compared to the draft.',
      entries: 'Entries ({{count}})',
      excluded: 'do not invoice',
      days: '{{count}} day texts changed',
      phases: 'Progress',
      extras: 'Further lines: {{count}} rows',
      problems: '{{count}} rows were not taken over:',
      row: 'row {{row}}',
      problem: {
        hours: 'invalid hours (0 to 24)',
        percent: 'invalid progress (0 to 100)',
        line: 'further line incomplete',
        date: 'invalid date',
        number: 'invalid number',
        unknown: 'entry or work stage does not belong to the document',
        own: 'own entries stay unchanged (someone else decides on your own hours)',
        too_many: 'too many further lines (at most 30)',
      },
    },
    recipient: {
      button: 'Recipient from customer data',
      title: 'Recipient from the customer data',
      none: 'No firm is linked to this project yet (Customers > Projects).',
      agreed: 'per terms',
      take: 'Take over',
    },
  },
  workload: {
    leadPrivacy: 'You see the load level (colour), not contract hours. Hours are visible only to HR and management.',
    cellTitleLead: 'planned {{plan}} h, booked {{booked}} h',
    levelTitle: 'Load: {{level}}',
    levelShort: {
      none: 'none',
      low: '< 70 %',
      ok: '70-100 %',
      high: '100-110 %',
      over: '> 110 %',
    },
    title: 'Workload',
    subtitle: 'Capacity = contract hours minus public holidays (per location) and absences.',
    level: {
      none: 'no capacity',
      low: 'below 70 %',
      ok: '70 to 100 %',
      high: '100 to 110 %',
      over: 'above 110 %',
    },
    legend: 'Value relative to capacity',
    cellTitle: 'Capacity {{cap}} h, planned {{plan}} h, booked {{booked}} h',
    internalPart: 'thereof internal (R&D, training, administration): {{hours}} h',
    location: 'Location',
    planHint: 'Enter hours per week for this project. The colour shows the person\'s load across all projects.',
    fill: 'Fill series',
    fillTitle: 'Plan several weeks',
    perWeek: 'Hours per week',
    import: 'Import CSV',
    importTitle: 'Import absences',
    importResult: '{{ok}} imported, {{errors}} errors',
    calendar: 'Calendar',
    overrides: 'Company adjustments',
    overrideNew: 'Adjust day',
    fraction: 'Time off',
    fractionFull: 'full day',
    fractionHalf: 'half day (afternoon off)',
    fractionNone: 'working day (remove holiday)',
    allCalendars: 'all calendars',
    rowsFound: '{{count}} rows found',
    rowsFound_one: '{{count}} row found',
    noAccess: 'Workload is visible to project leads (their projects), planners and management.',
    onlyFlagged: 'Only with notes',
    stateHint: 'Finished weeks: note when bookings differ from capacity by more than {{hours}} h (all projects).',
  },
  kpi: {
    solo: 'Worked on mostly alone',
    soloHint: '{{count}} tasks with at least 80 % of the hours from this person; only here the ratio says something about the person\'s own work.',
    soloHint_one: '{{count}} task with at least 80 % of the hours from this person; only here the ratio says something about the person\'s own work.',
    confidentialNote: 'Personal performance data. Management only; every access is logged.',
    utilization: 'Utilization',
    utilizationHint: 'booked / capacity',
    billableShare: 'Billable share',
    projectShare: 'Project work share',
    balance: 'Balance target/actual',
    punctuality: 'Submitted on time',
    rejectionRate: 'Corrections requested',
    capacity: 'Target',
    booked: 'Actual',
    billable: 'Billable',
    budgetUsed: 'Budget used',
    salesValue: 'Billable value',
    unbilledValue: 'Not billed yet',
    cost: 'Cost',
    costMissing: 'without cost rate: {{hours}} h',
    byPerson: 'By person',
    byMonth: 'By month',
    byProject: 'By project',
    weekly: 'Weekly trend',
    plannedRemaining: 'Still planned',
    otherHours: 'Other hours',
    showTable: 'Show table',
    showChart: 'Show chart',
    weekPrefix: 'CW',
    asOf: 'Target/actual {{from}} to {{to}}',
    weekState: 'Week',
    under: '{{hours}} h below target',
    over: '{{hours}} h above target',
    underLegend: 'Week below target',
    overLegend: 'Week above target',
    efficiency: 'Plan/actual ratio',
    efficiencyHint: '{{count}} tasks: {{plan}} h planned share, {{actual}} h booked',
    efficiencyHint_one: '{{count}} task: {{plan}} h planned share, {{actual}} h booked',
    efficiencyNone: 'No finished tasks with a plan in this period.',
    efficiencyExplain: 'Finished tasks with a plan (Forma field or value set by the project lead). Ratio = plan / all hours booked on the task; above 100 % = faster than planned. The value belongs to the task: everybody who worked on a task has the same value. It shows how the tasks worked on went against their plan, not how fast a person works.',
    efficiencyExplainOwn: 'Finished tasks with a plan (Forma field or value set by the project lead) that only you booked on. Ratio = plan / your hours; above 100 % = faster than planned. Tasks worked on together do not appear here, because their ratio would reveal the hours of the others.',
    sharedLeftOut: '{{count}} tasks worked on together are not included.',
    sharedLeftOut_one: '{{count}} task worked on together is not included.',
  },
  projects: {
    openBookingImportHint: 'Unticked, only members of the project team can book; the office or the project lead sets the team in the project.',
    importForma: 'Import from Forma',
    importInfo: 'Creates a Projektzeit project for each selected Forma project, links it and switches synchronisation on. Number and name come from the Forma name (e.g. "2024-017 Klinikum Nordtal"). Tasks and locations appear after the next synchronisation. Add project lead, members and fee per project afterwards.',
    importResult: '{{created}} imported, {{skipped}} skipped (already linked)',
    importDone: '{{count}} projects imported',
    importDone_one: '{{count}} project imported',
    importCount: 'Import {{count}}',
    importAs: 'In Projektzeit',
    alreadyLinked: 'linked to {{number}}',
    autoNumber: 'F number',
    archivedMany: '{{count}} projects archived',
    archivedMany_one: '{{count}} project archived',
    restoredMany: '{{count}} projects restored',
    restoredMany_one: '{{count}} project restored',
    archiveCount: 'Archive {{count}}',
    restoreCount: 'Restore {{count}}',
    title: 'Projects',
    new: 'Create project',
    number: 'Project number',
    name: 'Name',
    customer: 'Client',
    customerAddress: 'Client address',
    partner: 'Partner',
    site: 'Site',
    calendar: 'Holiday calendar (site)',
    billingMode: {
      label: 'Billing',
      hourly: 'By hours',
      fixed_fee: 'Fixed fee',
      mixed: 'Mixed',
      internal: 'Internal',
    },
    defaultBillingType: 'Default billing type',
    openBooking: 'All employees may book',
    openBookingHint: 'For internal projects such as training or administration.',
    budget: 'Budget (hours)',
    fee: 'Total fee',
    start: 'Start',
    end: 'End',
    statusLabel: 'Status',
    syncPausedHint: 'Only active projects are synchronised with Forma automatically. Paused, completed, cancelled and archived projects rest; "Sync now" works at any time, and when a project is set back to active the next run reads the whole project.',
    statuses: {
      active: 'Active',
      on_hold: 'On hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    notes: 'Notes',
    color: 'Colour',
    colorHint: 'Recognition colour in "My week" and in the lists, always with number and name. New projects get the least used colour.',
    colorNone: 'none',
    colors: {
      blue: 'Blue',
      orange: 'Orange',
      aqua: 'Aqua',
      yellow: 'Yellow',
      magenta: 'Magenta',
      green: 'Green',
      violet: 'Violet',
      red: 'Red',
    },
    assignColors: 'Assign colours',
    assignColorsHint: 'Active projects without a colour each get the least used colour.',
    assignedColors: '{{count}} projects got a colour',
    approval: {
      title: 'Approval of hours',
      mode: 'Who approves',
      companyDefault: 'Company default ({{mode}})',
      self: 'Approve own hours (approvers)',
      selfHint: 'May project lead and office approve their own hours in this project?',
      selfDefault: 'Company default ({{value}})',
      selfAllowed: 'Approvers may approve their own hours.',
      changeHint: 'A change applies to all entries not decided yet; entries approved already stay approved.',
      modes: {
        office: 'Office (one stage)',
        lead: 'Project lead (one stage)',
        lead_office: 'Project lead, then office (two stages)',
        self: 'Own approval with the submission',
      },
      hints: {
        office: 'The office approves the submitted hours; the project lead is not asked.',
        lead: 'The project lead approves; without another project lead the office decides.',
        lead_office: 'First the project lead, then the office.',
        self: 'Submitting approves the hours. The person can take that back until the month is closed; the office can correct.',
      },
    },
    archiveText: 'Archived projects disappear from lists and pickers. Data and the Forma link are kept.',
    tabs: {
      overview: 'Overview',
      members: 'Team',
      tasks: 'Tasks',
      forma: 'Forma',
      fee: 'Fee',
      kpi: 'Hours and budget',
      analysis: 'Analysis',
    },
    members: 'Team',
    addMember: 'Add person',
    role: {
      lead: 'Project lead',
      member: 'Member',
    },
    listScope: {
      label: 'Which projects',
      mine: 'My projects',
      all: 'All projects',
      mineHint: 'Projects in which you are a member or the project lead. "All projects" shows every project you may see; the choice applies to your account on every device.',
    },
    scope: {
      label: 'Tasks',
      all: 'all',
      selected: 'selected only',
    },
    validFrom: 'From',
    validTo: 'Until',
    removeMember: 'Remove',
    removeMemberText: 'If the person has bookings, the membership ends today; the bookings stay.',
    selectTasks: 'Select tasks',
    editMember: 'Edit membership',
    scopeSelectedHint: 'The person can then book only on selected tasks. The task selection opens after saving.',
    noTasksSelected: 'No task selected: the person cannot book on this project.',
    validRangeError: '"Until" is before "From".',
    myRole: 'My role',
    formaLinked: 'Linked to Forma',
    formaNotLinked: 'Not linked to Forma',
    formaState: {
      not_linked: 'Not linked',
      active: 'Linked',
      deleted: 'Deleted in Forma',
      access_lost: 'No access in Forma',
    },
    formaStateHint: 'The platform never deletes data because of changes in Forma. Affected tasks stay bookable and are marked.',
    formaLink: 'Link Forma project',
    formaUnlink: 'Unlink',
    formaProject: 'Forma project',
    syncEnabled: 'Import issues automatically',
    includeClosed: 'Import closed issues',
    syncNow: 'Sync now',
    fullSync: 'Full sync',
    lastSync: 'Last sync',
    syncResult: '{{created}} new, {{updated}} changed, {{flagged}} flagged, {{excluded}} not taken over by the filter',
    feePhases: 'Fee by work stage (HOAI § 55 building services)',
    feeHint: 'Weights and amounts can be changed, e.g. for reductions or special services.',
    resetHoai: 'Use HOAI standard',
    runStats: '{{fetched}} read, {{created}} new, {{updated}} changed, {{excluded}} not taken over',
    whatIsRead: 'What is read from Forma',
    readFields: 'Issues with number, title, status, category and type, location, start and due date, and the fields chosen below.',
    readNotHours: 'Planned and booked hours come only from Projektzeit, not from Forma.',
    readOneWay: 'Read only: Projektzeit never writes to Forma.',
    includeClosedHint: 'Recommended: take over finished issues too, so progress and bookings are complete.',
    importFilter: 'Categories and types (import filter)',
    clashInfo: 'Clashes of the model coordination (Forma Model Coordination, Navisworks add-in) are created by Forma as issues in the category "Coordination" with the locked type "Clash". Types with this name or "Kollision" are not taken over by default. Choose here which types appear in Projektzeit.',
    saveFilter: 'Save and sync',
    filterSaved: 'Saved: {{hidden}} hidden, {{shown}} shown again, {{kept}} kept because of hours',
    cleanupExcluded: 'Clean up hidden ({{count}})',
    noTypesYet: 'Categories and types are read from Forma at the next sync.',
    take: 'Take over',
    categoryType: 'Category / type',
    inProjektzeit: 'Visible',
    hiddenByFilter: 'Hidden',
    withHours: 'With hours',
    clashBadge: 'Clash',
    inactiveInForma: 'inactive in Forma',
    notTaken: 'not taken over',
    showTasks: 'Show in task list',
    lastFullExcluded: 'Not taken over by the filter in the last full sync ({{date}}): {{count}} issues.',
    keptWithHours: '{{count}} tasks of excluded types stay visible because hours were booked on them.',
    filterHint: 'Excluding hides the tasks at once (reversible). "Clean up" deletes hidden tasks without hours from Projektzeit; they stay in Forma and come back when the type is taken over again.',
    cleanupTitle: 'Clean up hidden tasks',
    cleanupText: '{{count}} hidden tasks of the excluded types without booked hours are deleted from Projektzeit. Nothing changes in Forma. Tasks with hours are kept.',
    cleanupConfirm: 'Clean up',
    cleanupDone: '{{deleted}} tasks deleted, {{kept}} kept because of hours',
    fieldsTitle: 'Fields from Forma ({{taken}} of {{total}} taken over)',
    fieldsIntro: 'Custom fields of the Forma issues, e.g. trade, floor, work stage, priority, sub-project, sprints. Fields taken over appear in the task lists as column and filter and in the project analysis.',
    reloadFields: 'Read fields again',
    fieldsReloaded: '{{count}} fields read',
    saveFields: 'Save and sync',
    fieldsSaved: 'Fields saved; the sync reads the values',
    searchFields: 'Search field',
    onlyTaken: 'Only taken over',
    noFieldsYet: 'The fields are read from Forma at the next sync.',
    field: 'Field',
    fieldType: 'Type',
    fieldValues: 'Values',
    fieldRole: 'In the analysis',
    fieldDeleted: 'deleted in Forma',
    locationsUsed: 'The analysis takes building and floor from the Forma locations (building: level {{building}}, floor: level {{floor}}).',
    fieldsHint: 'Values are read only and updated on every sync. Which field serves as floor, building, trade or work stage is suggested and can be changed in the project analysis under "Settings".',
    fieldRoles: {
      floor: 'Floor',
      building: 'Building',
      trade: 'Trade',
      phase: 'Work stage',
    },
    fieldTypes: {
      list: 'List',
      text: 'Text',
      numeric: 'Number',
      date: 'Date',
      paragraph: 'Paragraph',
      checkbox: 'Checkbox',
    },
    count: '{{count}} projects',
    count_one: '{{count}} project',
    archiveFilter: 'Show',
    archiveModes: {
      active: 'Current projects',
      only: 'Archived only',
      all: 'All',
    },
    booking: {
      title: 'Booking',
      open: 'Bookable',
      openHint: 'Employees find the project and its tasks when booking.',
      closedShort: 'Not bookable',
      closedHint: 'Without an active Forma connection and without a release, the project is hidden when people search for projects and tasks.',
      release: 'Bookable without Forma',
      releaseHint: 'Releases the project for booking without a Forma connection (e.g. acquisition or an older project). Can be taken back at any time.',
    },
    identityOnlyHint: 'As technical admin you change the project number and name; the office maintains everything else.',
    includeDrafts: 'Take over drafts from Forma',
    includeDraftsHint: 'Off (default): issues in draft status do not become tasks; drafts already created without hours are hidden.',
    formaNameFollow: 'Take over number and name from Forma',
    formaNameFollowHint: 'A rename in Forma changes the project number and name here at the next sync. Off: the values kept here stay.',
    runDrafts: '{{count}} drafts not taken over',
    runDrafts_one: '1 draft not taken over',
  },
  people: {
    protectedHint: 'This account holds protected rights that you do not have. Role, access, e-mail and activation are changed only by someone who holds these rights (usually management); emergency access only on the server.',
    weeklyHoursHint: 'Visible only to the person, HR and management.',
    title: 'People',
    new: 'Create person',
    displayName: 'Name',
    shortCode: 'Initials',
    kind: {
      external: 'External',
    },
    company: 'Company (external)',
    discipline: 'Discipline',
    country: 'Location',
    countries: {
      DE: 'Germany',
      TR: 'Turkey',
    },
    calendar: 'Holiday calendar',
    entryDate: 'Start date',
    exitDate: 'End date',
    notes: 'Notes (HR)',
    contracts: 'Working time',
    weeklyHours: 'Hours per week',
    workdays: 'Working days',
    addContract: 'Change from date',
    rates: 'Hourly rates',
    rateKind: {
      sales: 'Sales',
      cost: 'Cost',
    },
    rateProject: 'project only',
    perHour: '€/h',
    roles: {
      admin: 'Administration (IT)',
      office: 'Office and management',
      employee: 'Employee',
      external: 'External',
    },
    ownRightsHint: 'Your own role and rights are changed by another authorized person. Protected rights are granted only by someone who holds them.',
    extraPermissions: 'Additional permissions',
    permissions: {
      'time.approve.final': 'Final approval of hours',
      'time.manage': 'Correct all hours',
      'period.manage': 'Month closing',
      'billing.manage': 'Billing',
      'rates.sales.view': 'See sales rates',
      'rates.cost.view': 'See cost rates',
      'hr.manage': 'HR: contract hours, absences (confidential)',
      'kpi.team.view': 'Team key figures (confidential)',
      'workload.manage': 'Plan workload',
      'projects.manage': 'Manage projects',
      'users.manage': 'Users and roles',
      'settings.manage': 'Settings',
      'backup.manage': 'Backups',
      'audit.view': 'View change log',
      'data.export.all': 'Full export and Power BI',
      'sync.manage': 'Forma sync',
      'analytics.view': 'Project analysis of all projects (management)',
      'customers.manage': 'Customer portfolio and terms',
      'announcements.manage': 'Publish announcements',
      'rights.manage': 'Assign roles and rights',
    },
    permissionHints: {
      'time.approve.final': 'Final approval of the hours of all projects (project leads approve their projects without this right).',
      'time.manage': 'Create, change and delete entries of others, also approved ones (logged).',
      'period.manage': 'Close and reopen months.',
      'billing.manage': 'Timesheets, instalments, hours to invoice.',
      'rates.sales.view': 'See sales rates and amounts on documents and in the analysis.',
      'rates.cost.view': 'See cost rates and costs in the analysis.',
      'hr.manage': 'Contracts, absences of everybody, sick reports, extra hours and holidays.',
      'kpi.team.view': 'Staff review (confidential, every view is logged).',
      'workload.manage': 'Planning of all projects and workload of all people.',
      'projects.manage': 'Create and change projects, set project leads and teams, templates.',
      'users.manage': 'Create accounts, reset access, activate and deactivate.',
      'settings.manage': 'System settings, categories, Outlook connection.',
      'backup.manage': 'Create and restore backups.',
      'audit.view': 'Read the log (amounts, hours and health data only with the matching right).',
      'data.export.all': 'Full export and Power BI data.',
      'sync.manage': 'Forma connection and synchronisation, fields taken over.',
      'analytics.view': 'Project analysis and portfolio of all projects.',
      'customers.manage': 'Customers, contacts and terms.',
      'announcements.manage': 'Publish announcements to everybody or groups.',
      'rights.manage': 'Give roles and rights (protected rights only by those who hold them).',
    },
    roleBase: {
      employee: 'no special rights: own hours and week, own absences, team calendar, tasks and projects the person is a member of. As project lead (set in the project) also approval, planning and analysis of the own projects – no right is needed for that.',
      external: 'no special rights: book only on the assigned projects and tasks, own entries; no team calendar, no figures.',
      office: 'all business rights of the office and the management (approval, billing, HR, projects, reports). Giving roles and rights is not part of it.',
      admin: 'technical administration: accounts, rights (no protected ones), settings, Forma, backup and log. No approvals, billing, HR or performance data.',
    },
    roleMatrix: {
      title: 'Default rights of all roles',
      protected: 'Protected right: only given by those who hold it',
      hint: 'Every right is a default of at least one role. Employees and externals deliberately have no default rights; what they can do without rights is shown above. The management is office plus "give roles and rights".',
    },
    activate: 'Activate',
    deactivate: 'Deactivate',
    resetAccess: 'Reset access',
    setupLink: 'Setup link',
    setupLinkHint: 'Hand the link over personally. It is valid for 7 days and works once.',
    memberships: 'Projects',
    absenceSummary: 'Absences {{year}}',
    days: '{{count, number}} days',
    days_one: '{{count, number}} day',
    access: {
      title: 'Access',
      password: 'Password set',
      totp: 'Second factor active',
      pending: 'Setup pending',
      entra: 'Microsoft sign-in linked',
    },
    tabs: {
      master: 'Master data',
      time: 'Working time',
      rates: 'Rates',
      access: 'Role and access',
    },
    weekly: '{{hours}} h/week',
    accessHint: 'Nobody hands out passwords directly: "Reset access" creates a setup link with which the person sets a password.',
    removePicture: 'Remove profile picture',
    approver: {
      title: 'Approval of absences',
      hint: 'Leave and other requests are decided by the chosen person (e.g. team lead). HR always confirms sick reports. Without a choice HR decides.',
      hr: 'HR',
    },
    subtitle: 'Master data, role and access. Absences, leave balances and calendars are in the tab “Absences”.',
    searchPlaceholder: 'Search name, code, discipline or e-mail',
    count: '{{count}} people',
    count_one: '{{count}} person',
    shortCodeHint: 'Fixed code of the person in timesheets and lists (e.g. HA, HAL): 2 to 6 letters or digits, unique in the company.',
    shortCodeAuto: 'automatic',
    suggestCode: 'Suggest from the name',
    fillCodes: 'Assign missing codes ({{count}})',
    fillCodesHint: 'Gives every active person without a code a unique one: the initials (HA); if taken, one more letter is added (HAL).',
    fillCodesDone: '{{count}} codes assigned',
    noAccess: 'Only for HR and system administration.',
    rights: 'Rights',
    rightsHint: 'The role sets default rights (preselected). Single defaults can be removed and further rights added. Only administration and management change roles and rights.',
    rightsDefault: 'Role defaults',
    rightsReadOnly: 'Only administration and management change roles and rights.',
    rightTags: {
      default: 'default',
      removed: 'removed',
      extra: 'added',
    },
    jobTitle: 'Job title',
    jobTitleHint: 'For display only; rights do not depend on it.',
    overtimeAccount: 'Keep an overtime account',
    overtimeAccountHint: 'Off: no overtime balance and no time off in lieu (e.g. managing directors or flat-rate pay).',
  },
  admin: {
    outlook: {
      title: 'Outlook calendars',
      notConfigured: 'The Microsoft 365 connection is not set up (GRAPH_* in .env). See the installation guide.',
      off: 'The transfer is switched off in the settings.',
      connection: 'Connection',
      targets: 'Targets',
      personal: 'personal calendars',
      appointments: 'Appointments in Outlook',
      failing: '{{count}} with errors',
      failing_one: '{{count}} with an error',
      lastRun: 'Last run',
      result: 'Result',
      resultText: '{{created}} new, {{updated}} changed, {{removed}} removed, {{errors}} errors',
      syncNow: 'Transfer now',
      hint: 'Runs every 5 minutes. Only approved absences from 7 days back to about 13 months ahead are transferred, without notes. Kinds not released for the team calendar, and sickness, appear as "Absent".',
      problems: 'Mailboxes with errors',
      noProblems: 'No errors.',
    },
    title: 'Administration',
    tabs: {
      outlook: 'Outlook',
      settings: 'Settings',
      periods: 'Month closing',
      forma: 'Forma',
      backup: 'Backups',
      audit: 'Change log',
      categories: 'Categories',
      templates: 'Task templates',
      jobs: 'Maintenance reports',
    },
    settings: {
      leave: 'Absences',
      teamVisible: 'Shown by kind in the team calendar',
      teamVisibleHint: 'All other kinds appear as "absent". Sickness is never shown by kind.',
      outlook: 'Outlook calendars',
      outlookEnabled: 'Write approved absences to Outlook',
      outlookPersonal: 'In the person\'s calendar with the status "Out of office" (visible when meetings are planned in Outlook and Teams)',
      outlookTeam: 'Shared team calendar (mailbox address, optional)',
      outlookHint: 'Requires the app registration and the Exchange role assignment, see the installation guide.',
      syncInterval: 'Forma sync every',
      syncWindow: 'Time window',
      windows: {
        daily: 'Daily 6 am to midnight, Sundays included',
        workhours: 'Mon-Sat 6 am-9 pm',
        always: 'Around the clock',
      },
      minutes: '{{count}} minutes',
      autoImport: 'Take over new Forma projects at night (bookable only for the project team)',
      company: 'Company',
      appName: 'Application name',
      place: 'Place (for documents)',
      defaultCalendar: 'Default holiday calendar',
      approval: 'Approval',
      stages: 'Approval stages',
      stages1: '1 stage (project lead or office)',
      stages2: '2 stages (project lead, then office)',
      selfApproval: 'Allow approving own hours',
      approvalMode: 'Default approval of hours',
      approvalModeHint: 'Applies to projects without a choice of their own. Each project can have its own rule under Overview > Approval of hours.',
      selfApprovalHint: 'Default for all projects; a project can set it differently.',
      time: 'Time recording',
      step: 'Step (minutes)',
      requireDescription: 'Description required for',
      ownBillingType: 'Employees choose the billing type when booking (billable, included in the fee, not billable). The rule of task and project is the default; fixed rules of a category or task stay fixed. Hours to invoice are still set by billing staff.',
      warnDaily: 'Warning above hours per day',
      maxFuture: 'Booking into the future (days)',
      deadline: 'Submit within (days after week end)',
      trackingStart: 'Tracking start (go-live)',
      trackingStartHint: 'Target/actual figures and capacity data for Power BI count from this day.',
      billing: 'Billing',
      prefixSn: 'Number prefix timesheet',
      prefixAb: 'Number prefix progress invoice',
      vat: 'VAT %',
      issuerName: 'Company name in documents',
      issuerAddress: 'Address in documents',
      billingLanguage: 'Default document language',
      forma: 'Forma',
      deepLink: 'Link template for issues',
      security: 'Security',
      totpExternal: 'Second factor for externals',
      teamKpi: 'Team key figures (personal performance data) enabled. Switch off until, for example, a works agreement is in place.',
      leadLoadLevels: 'Load levels for project leads and planners too (colours without hours). Someone who changes planned hours on purpose can roughly derive a person\'s available hours from the colour change; switch off if that is not wanted.',
      totpPrivileged: 'Second factor for office, management, admin',
      idle: 'Sign out after inactivity (hours)',
      maxDays: 'Maximum session (days)',
      bi: 'Power BI',
      pseudonymize: 'Replace names with initials in the Power BI export',
      companyName: 'Company name',
      companyNameHint: 'Shown in the navigation, on the sign-in page and in exports.',
      logo: 'Logo',
      logoHint: 'PNG, JPG or SVG; reduced to at most 600 × 200 pixels and saved at once.',
      noLogo: 'no logo',
      logoUpload: 'Upload logo',
      logoChange: 'Replace logo',
      workLocations: 'Work locations (choice when booking)',
      workLocationsHint: 'Fixed list instead of free text. The first value is the default for new entries.',
      hrKpi: 'Figures',
      tolerance: 'Note from deviation per week (hours)',
      toleranceHint: 'Finished weeks with a larger deviation from capacity are marked "below target" or "above target".',
      snDefaultHint: 'Default for new timesheets; can be changed in the document.',
      docSick: 'Certificate for sickness from calendar day',
      docChildSick: 'Certificate for sick child from calendar day',
      docHint: '0 = no certificate expected. If an expected certificate is missing, the person and HR see a notice; the sick report counts anyway.',
      retention: 'Keep documents (months after the end of the absence)',
      retentionHint: '0 = keep without limit. After that the nightly maintenance deletes the content of the files; name and time stay in the log.',
      saturdayLeaveTR: 'Turkey: Saturday inside leave counts as a leave day',
      saturdayLeaveTRHint: 'For people on the Turkish holiday calendar with a 5-day week: a Saturday between two leave days counts as a leave day when the employment contract does not make Saturday a weekly rest day (hafta tatili) (Yargıtay; Labour Law art. 56). Off: only the working days of the contract count (more favourable for employees). Sundays, public holidays and holidays on a Saturday never count; the target hours do not change.',
      leaveRulesHint: 'Leave days count only on working days of the contract; weekends, public holidays of the person\'s calendar and days off do not count. If someone falls ill during leave, HR enters the sickness with the doctor\'s note: the leave days are credited back (§ 9 BUrlG; Turkey: Labour Act art. 56, Annual Paid Leave Regulation art. 6), the leave is not extended. Details: Help › Absences.',
      standardsUrl: 'Link to the company standards',
      standardsUrlHint: 'Page where the company keeps its guidelines, templates and layouts (e.g. SharePoint). The start page only links to it; nothing is copied into the platform.',
      links: 'Links in the side bar',
      linksHint: 'Shown above the own name, only when an address is set. Without a label of your own the standard label of each language applies. Nothing is copied into the platform.',
      linkUrl: {
        standards: 'Company standards and templates (address)',
        meetings: 'Notes of the coordination meeting (address)',
        website: 'Company website (address)',
      },
      linkLabel: 'Label (optional)',
      automations: 'Automations',
      automationsIntro: 'Reminders the platform creates itself: no external service and no e-mail. Every rule shows a person only what they may see anyway.',
      autoWeekly: 'Deadline to submit the week (employees)',
      autoMissing: 'Missing bookings: working days without an entry (employees)',
      autoMissingDays: 'Look back (working days)',
      autoPending: 'Waiting approvals (approvers)',
      autoPendingDays: 'Highlight after (days)',
      autoMonth: 'Month closing in the first working days (office, management)',
      jobsTitle: 'Background jobs (display only)',
      jobForma: 'Forma synchronisation',
      jobFormaWhen: 'every {{minutes}} minutes, {{window}}',
      jobOutlook: 'Outlook calendars',
      jobOutlookWhen: 'every 5 minutes when the connection is switched on',
      jobNightly: 'Nightly run',
      jobNightlyWhen: '2:30 am: synchronisation, snapshot, capacity values, retention periods, Power BI export (when set up)',
      jobsHint: 'These jobs always run. Set rhythm and time window of the Forma synchronisation in the "Forma / Power BI" panel.',
      paymentDays: 'Default payment term (days)',
      paymentDaysHint: 'Default payment term when the terms of a project are created. The terms per project are under Customers › Terms.',
      noOvertime: 'Allow hours that do not count as extra hours',
      noOvertimeHint: 'Entries and categories can be marked as "does not count as extra hours" (e.g. voluntary training in the evening). The hours stay booked and billable.',
    },
    periods: {
      hint: 'A closed month cannot be changed any more. Resolve open entries first.',
      lock: 'Close',
      unlock: 'Reopen',
      locked: 'Closed',
      open: 'Open',
      openEntries: 'Drafts',
      submitted: 'In review',
      approved: 'Approved',
      billed: 'Billed',
      forceTitle: 'Close the month despite open entries?',
      forceText: '{{count}} entries are not approved. They stay unchanged in the closed month.',
      forceText_one: '{{count}} entry is not approved. It stays unchanged in the closed month.',
      unlockTitle: 'Reopen month',
    },
    forma: {
      connStatus: {
        connected: 'connected',
        disconnected: 'not connected',
        error: 'error',
      },
      nextRun: 'Next sync',
      load: 'Each run: {{projects}} active projects, about {{requests}} requests, about {{minutes}} min; about {{perDay}} requests a day. Not synchronised: {{skipped}} paused, completed, cancelled or archived projects. Projektzeit sends at most {{rpm}} requests per minute and waits when Autodesk asks for a pause.',
      loadWarn: 'A run takes almost the whole interval: choose a longer interval or set finished projects to completed.',
      connection: 'Connection',
      mode: 'Mode',
      modes: {
        mock: 'Demo (fictional data)',
        user_3lo: 'Service user (sign-in)',
        ssa: 'Secure Service Account',
      },
      hub: 'Hub',
      connect: 'Sign in to Forma',
      notConfigured: 'Forma is not configured (FORMA_MODE). See the installation guide.',
      projects: 'Linked projects',
      runs: 'Recent syncs',
      syncAll: 'Sync all',
      fullAll: 'Full sync of all projects',
      kind: {
        full: 'full',
        incremental: 'changes',
      },
      statsHeader: 'fetched / new / changed / flagged',
      duration: 'Duration',
      token: 'Sign-in valid until',
      runStatus: {
        ok: 'ok',
        warning: 'note',
        error: 'error',
        running: 'running',
      },
    },
    backup: {
      countsOnly: 'Number of rows per table. Changed entries with person, date and hours are shown only to people with the right "Full export and Power BI".',
      title: 'Backups',
      hint: 'The database is fully backed up every night (server). Here: snapshots for comparison and full exports.',
      snapshots: 'Snapshots',
      create: 'Create snapshot',
      label: 'Label',
      compare: 'Compare',
      compareWith: 'Compare with',
      current: 'current state',
      download: 'Download',
      exportAll: 'Full export',
      exportXlsx: 'Excel (all tables)',
      exportJson: 'JSON (complete)',
      powerbi: 'Power BI',
      powerbiHint: 'Optional: CSV files for your own Power BI reports. If SharePoint is set up, they are written there every night; here manually. Not needed for the analyses in Projektzeit.',
      powerbiDownload: 'Create CSV package',
      powerbiUpload: 'Upload to SharePoint',
      result: 'Comparison',
      protectedOk: 'No protected data removed.',
      protectedBad: '{{count}} protected records are missing. Check immediately.',
      protectedBad_one: '{{count}} protected record is missing. Check immediately.',
      table: 'Table',
      before: 'Before',
      after: 'After',
      added: 'added',
      removed: 'removed',
      changed: 'changed',
      entryChanges: 'Changed time entries',
      monthTotals: 'Monthly totals with differences',
      kinds: {
        nightly: 'nightly',
        manual: 'manual',
        before_change: 'before change',
      },
      protected: 'protected',
      size: 'Size',
      entriesCount: 'Time entries',
    },
    audit: {
      masked: 'Content visible to authorised people only (e.g. hours, contract data, billing)',
      action: 'Action',
      entity: 'Object',
      who: 'Who',
      when: 'When',
      change: 'Change',
      shown: '{{count}} events shown',
      moreWhileScrolling: '{{count}} events shown, more while scrolling',
      partlyMasked: 'Some values visible to authorised people only (e.g. fee, budget, hours, rates)',
    },
  },
  profile: {
    title: 'Profile',
    language: 'Language',
    password: 'Change password',
    current: 'Current password',
    newPassword: 'New password',
    changed: 'Password changed',
    myExport: 'Export my hours',
    demoNote: 'The demo has no sign-in. Switch the role at the top.',
    picture: 'Profile picture',
    pictureUpload: 'Upload picture',
    pictureChange: 'Change picture',
    pictureHint: 'JPG, PNG or WebP. Cropped to a square and reduced. Visible to signed-in colleagues.',
    themeHint: 'Light, dark or as set in the operating system.',
    access: 'Access',
    accessHint: 'Access is set up by the administration. Forgot your password: the administration sends a new setup link.',
  },
  auth: {
    entraMismatch: 'This Microsoft account does not match the account linked to this e-mail. Please ask the administration to reset your access.',
    title: 'Sign in',
    email: 'Email',
    password: 'Password',
    totp: 'Code from the authenticator app',
    signIn: 'Sign in',
    signInMicrosoft: 'Sign in with Microsoft',
    or: 'or',
    setupTitle: 'Set up access',
    setupFor: 'Access for {{name}} ({{email}})',
    setPassword: 'Set password',
    passwordRules: 'At least 10 characters, three kinds of: lower case, upper case, digits, special characters.',
    totpSetupTitle: 'Set up second factor',
    totpSetupText: 'Scan the QR code with Microsoft Authenticator or another app and enter the code shown.',
    totpManual: 'Key for manual entry',
    enable: 'Activate',
    entraUnknown: 'This Microsoft account does not exist in the platform.',
    entraFailed: 'Microsoft sign-in failed.',
    noAccess: 'No access or forgot your password? The administration sends a setup link.',
  },
  overview: {
    fastTask: 'Faster than planned: {{ref}} {{title}} (plan {{plan}} h, your time {{own}} h)',
    fastCheck: 'Far below plan? Briefly check that all hours are booked.',
    overPlanTitle: 'Tasks over plan ({{count}})',
    overPlanHint: 'Open tasks with more booked than planned hours. Adjust the plan or clarify the cause.',
    hello: 'Hello, {{name}}',
    toWeek: 'Open week',
    openLastWeek: 'Last week: {{count}} entries not submitted',
    openLastWeek_one: 'Last week: {{count}} entry not submitted',
    openOlder: '{{count}} older drafts',
    openOlder_one: '{{count}} older draft',
    approvals: 'Waiting for approval',
    approvalsL1: 'With the project lead',
    approvalsFinal: 'With the office',
    toApprovals: 'Review',
    budget: 'Budget 80 % or more',
    forma: 'Notes from Forma',
    formaText: 'These tasks have open bookings but were deleted in Forma or are no longer visible.',
    billing: 'Billing',
    draftDocs: '{{count}} drafts',
    draftDocs_one: '{{count}} draft',
    unbilled: '{{hours}} h approved, not billed',
    system: 'System',
    syncErrors: 'Forma sync: {{count}} errors in 24 h',
    syncErrors_one: 'Forma sync: {{count}} error in 24 h',
    syncOk: 'Forma sync without errors',
    lastSnapshot: 'Last snapshot: {{when}}',
    mine: 'My area',
    lead: 'Management',
    toCockpit: 'Open cockpit',
    myWeekTitle: 'My week (CW {{week}})',
    remaining: '{{hours}} h left to target',
    weekFull: 'Target of this week reached',
    allGood: 'Everything submitted, nothing unusual.',
    weekUnder: 'CW {{week}}: {{hours}} h below your target hours. Bookings or an absence missing?',
    weekOver: 'CW {{week}}: {{hours}} h above your target hours (extra hours).',
    stateHint: 'Notice from {{hours}} h deviation per week. Public holidays and approved absences reduce the target hours.',
    myFigures: 'My key figures',
    periods: {
      month: 'Month',
      quarter: 'Quarter',
      year: 'Year',
    },
    targetActual: 'Actual / target',
    weekStates: 'Weeks below target: {{under}}, above: {{over}}',
    planVsActual: 'Tasks: plan and actual',
    showLess: 'Show less',
    showAll: 'Show all {{count}}',
    doneOn: 'Finished on',
    planTask: 'Plan (h)',
    actualTask: 'Actual total (h)',
    ownHours: 'My hours (h)',
    personHours: 'thereof person (h)',
    factor: 'Factor',
    leadQuiet: 'Nothing open.',
    layout: {
      button: 'Customize',
      title: 'Customize the start page',
      hint: 'Show or hide blocks and change their order. The setting applies to your account on every device.',
      reset: 'Default',
      up: 'Move up',
      down: 'Move down',
      tiles: 'Tiles',
      blocks: {
        tiles: 'Tiles',
        work: 'My work',
        week: 'My week',
        announcements: 'Announcements',
        upcoming: 'Coming up',
        figures: 'My figures',
        lead: 'For the management',
      },
    },
  },
  demo: {
    viewAs: 'View as',
    personasHint: 'Switch roles to compare permissions and visibility.',
    filePreview: 'Preview of the generated file',
    downloadBlocked: 'Direct downloads are blocked in the demo viewer: the file is shown here and can be saved with "Download".',
    formaLink: 'In the demo there is no real Forma project behind the issues. In the installed platform this link opens the issue in Autodesk Forma (new tab).',
    download: 'Download',
    downloadSaved: 'File saved',
    downloadFailed: 'The file could not be saved. Please try again.',
    downloadUnavailable: 'Downloads are not possible in this view: the file is only shown here.',
  },
  planning: {
    title: 'Planning',
    subtitle: 'Planned hours per task and staff per week. For project leads and management.',
    noProjects: 'No projects to plan. Project leads (their projects) and management can plan.',
    chooseProject: 'Choose project',
    openPlanning: 'Edit planned hours',
    tabs: {
      tasks: 'Tasks (planned hours)',
      staff: 'Staff per week',
    },
    tasksInfo: 'Planned hours are the basis for plan/actual per task; ideally enter them before work starts. They are kept only here, Forma delivers no hours. Empty = no plan yet, 0 = task is not evaluated. Every change is logged.',
    onlyOpen: 'Open tasks only',
    onlyWithoutPlan: 'Without plan only',
    planHours: 'Plan (h)',
    noPlan: 'no plan',
    legend: 'Enter hours, e.g. 12 or 12.5. Enter or leaving the field saves, Esc discards.',
    bulkSet: 'Set plan',
    bulkRemove: 'Remove own plan',
    bulkHint: 'Applies to {{count}} selected tasks. 0 = do not evaluate.',
    bulkHint_one: 'Applies to {{count}} selected task. 0 = do not evaluate.',
    bulkDone: '{{count}} tasks changed',
    bulkDone_one: '{{count}} task changed',
    location: 'Location',
    subLocation: 'Sub-location',
    site: 'Project site',
    noLocation: 'no location in Forma',
    filters: {
      status: 'Status',
      allStatus: 'All statuses',
      due: 'Due',
      dueAny: 'Any date',
      dueOverdue: 'Overdue',
      due7: 'Due within 7 days',
      due14: 'Due within 14 days',
      dueNone: 'No date',
      plan: 'Plan',
      planAny: 'Any plan',
      planNone: 'No plan',
      planZero: 'Not evaluated (0)',
      planSet: 'With plan',
      booked: 'Bookings',
      bookedAny: 'All',
      bookedNone: 'No bookings',
      bookedSome: 'With bookings',
      sort: 'Sort by',
      sortRef: 'Number',
      sortDue: 'Due date',
      sortBooked: 'Booked (most first)',
      sortPlan: 'Plan (largest first)',
      reset: 'Reset filters',
    },
  },
  evaluation: {
    title: 'Staff review',
    subtitle: 'Figures per person for reviews and steering. Who has time when is shown under Workload.',
    noAccess: 'Management only (right "Team figures").',
    signalFastTitle: 'Tasks clearly below plan',
    signalFastText: 'Finished tasks with a plan/actual ratio of {{percent}} % or more. Check the plan (too large?) or share the good solution in the team. The value belongs to the task, not to the person.',
    signalOverTitle: 'Above target hours',
    signalOverText: 'Weeks more than {{hours}} h above the target hours: extra hours, talk about the workload.',
    signalUnderTitle: 'Below target hours',
    signalUnderText: 'Weeks more than {{hours}} h below the target hours: clarify missing bookings or absences.',
    sort: 'Sort',
    sortBy: {
      name: 'Name',
      balance: 'Balance (lowest first)',
      billable: 'Billable share',
      flags: 'Notices (most first)',
    },
    hint: 'Plan/actual ratio = planned hours of finished tasks / hours booked on them. The same for everybody on a shared task; only the value of tasks worked on mostly alone says something about a person\'s own work. Meaningful only with several tasks and good plan quality; no ranking and on its own no basis for personnel decisions.',
    criteria: {
      title: 'Evaluation criteria',
      intro: 'These criteria underlie the evaluation. The selection only decides which columns this view shows (this device only); the figures themselves stay unchanged.',
      show: 'Criteria',
      all: 'Show all',
      balance: {
        name: 'Target time and balance',
        desc: 'Booked hours against the target time from contract, holidays and absences in the chosen period.',
      },
      weeks: {
        name: 'Week flags',
        desc: 'Finished weeks more than {{hours}} h under or over the target time (tolerance from the settings).',
      },
      billable: {
        name: 'Billable share',
        desc: 'Billable hours as a share of all booked hours (excluding fixed fee and non-billable).',
      },
      projectShare: {
        name: 'Project work share',
        desc: 'Share of project work in the booked hours; internal time (training, business development, admin) is shown separately.',
      },
      efficiency: {
        name: 'Plan/actual ratio',
        desc: 'Plan hours of finished tasks against the hours booked on them: green from {{good}} %, a note below {{low}} %. The value belongs to the task, not a ranking.',
      },
      punctuality: {
        name: 'Submitted on time',
        desc: 'Weeks submitted within the deadline ({{days}} day(s) after the weekend).',
      },
      corrections: {
        name: 'Corrections requested',
        desc: 'Share of entries that were sent back for correction during approval.',
      },
      thresholds: 'Thresholds: plan/actual green from {{good}} %, note below {{low}} %; week tolerance {{hours}} h; submission deadline {{days}} day(s).',
    },
  },
  absencesPage: {
    title: 'Absences and holidays',
    subtitle: 'Entitlements and balances, all absences, entry and import, extra hours for payroll, public holidays and company events. Requests are decided under Approvals.',
    noAccess: 'Only for HR (absences) and the administration (holidays, company events).',
    searchPerson: 'Search person',
  },
  areas: {
    title: 'Hours by area',
    titleMine: 'Where my time went',
    area: 'Area',
    project: 'Project work',
    internal: 'Internal',
    forma: 'Forma tasks',
    hintMine: 'Area = category of the manual task (e.g. meetings, site visits) or Forma tasks. Internal = time outside projects, e.g. training, R&D, administration. Visible only to you.',
    hintCockpit: 'Sum of all booked hours in the period, without reference to persons.',
    hintLead: 'Sum of the booked hours in your projects, without reference to persons.',
    shareHint: 'Share of hours on projects (without the internal project)',
    internalHours: 'internal {{hours}} h',
  },
  hourTypes: {
    title: 'Hour types',
    type: 'Type',
    billable: 'Billable',
    included: 'Included (flat fee)',
    nonBillableProject: 'Project, not billable',
    internal: 'Internal',
    termsTitle: 'Terms: planned, booked, billed and hour types',
    coverageShort: '{{hours}} h without plan',
    coverageTitle: 'The ratio covers {{pct}} of the hours on finished tasks. {{hours}} h on {{count}} finished tasks without plan hours are not evaluated.',
    coverageTitle_one: 'The ratio covers {{pct}} of the hours on finished tasks. {{hours}} h on {{count}} finished task without plan hours are not evaluated.',
    coverageNotice: 'Some finished tasks have no plan hours: in the team the plan/actual ratio covers only {{pct}} of the hours on finished tasks, {{hours}} h are not evaluated. Read values marked "without plan" with care; add plan hours in Forma or on the manual tasks.',
    coverageInfo: 'Some finished tasks have no plan hours ({{hours}} h in the team); these hours are not part of the plan/actual ratio.',
    terms: {
      plan: {
        name: 'Planned',
        desc: 'Plan hours of a task (Forma field or entry on the manual task). Only finished tasks with a plan count in the plan/actual ratio. Empty = not planned: not evaluated, but shown as "without plan"; 0 = deliberately not evaluated.',
      },
      booked: {
        name: 'Booked (actual)',
        desc: 'The hours a person enters. Basis for target/actual, balance, utilisation and the plan/actual ratio. Personal figures use booked hours only.',
      },
      billed: {
        name: 'Billed',
        desc: 'The hours the office takes over to the timesheet; they may differ from the booked hours. They are shown in project analysis and billing (booked against billed), not in the evaluation of a person.',
      },
      billable: {
        name: 'Billable',
        desc: 'Project work that appears on the customer document by the hour.',
      },
      included: {
        name: 'Included (flat fee)',
        desc: 'Project work covered by the flat fee: on the timesheet of a flat-fee project, not charged separately.',
      },
      nonBillableProject: {
        name: 'Project, not billable',
        desc: 'Work in a customer project that is not charged (e.g. an internal check). It counts as project work but never appears on a customer document. The type comes from the task or its category.',
      },
      internal: {
        name: 'Internal',
        desc: 'Time on the internal project, e.g. research and development, training, administration: not project work and never billable. It counts towards the target like any booking.',
      },
      example: {
        name: 'Example',
        desc: 'Task with 20 h plan, 5 h booked and finished, 8 h billed: plan/actual ratio 400 % (well under plan), target/actual of the person +5 h, project analysis 5 h booked and 8 h billed.',
      },
    },
  },
  scope: {
    button: 'Limit values',
    col: 'In the project',
    title: 'Values of "{{field}}" in the project',
    intro: 'Which values of this field the project uses: in the whole project, per building and per location from Forma. The project analysis uses only these values; a task with another value counts there as "no value" and is listed. The tasks in Forma stay unchanged.',
    targets: 'Scope',
    project: 'Whole project',
    buildings: 'Buildings ({{field}})',
    locations: 'Locations from Forma',
    all: 'all',
    inherit: 'inherited',
    count: '{{n}} of {{m}}',
    selectAll: 'All',
    selectNone: 'None (inherit)',
    reset: 'All values',
    hintProject: 'Values not selected are not used by the project. All selected = no limit.',
    hintPlace: 'The values this building or location uses (e.g. its floors). Without a selection the values of the location above, the building or the whole project apply.',
    tasks: '{{count}} tasks',
    notOffered: 'no longer in Forma',
    floorHint: 'Floors of this selection without tasks appear empty (hatched) in the building of the analysis, floors outside it not at all.',
    saved: 'Values saved. {{count}} tasks have values outside them and are listed in the analysis.',
    saved_one: 'Values saved. {{count}} task has a value outside them and is listed in the analysis.',
    savedNone: 'Values saved.',
    summaryAll: 'all values',
    summaryPlaces: '+ {{count}} buildings/locations',
    summaryPlaces_one: '+ {{count}} building/location',
    notice: '{{count}} tasks have values outside the values of the project: {{list}}. They count here as "no value", so that no floor or trade appears that the project does not have. Correct the value in Forma or adjust the values of the project.',
    notice_one: '{{count}} task has a value outside the values of the project: {{list}}. It counts here as "no value", so that no floor or trade appears that the project does not have. Correct the value in Forma or adjust the values of the project.',
    edit: 'Edit values',
    taskMark: 'Outside the values of the project: {{list}}',
  },
  staffing: {
    title: 'Who works where',
    intro: 'Projects and the people in them: booked hours in the period (the darker, the more), project lead (PL) and members without hours. Drafts of others do not count, the internal project is not included.',
    introLead: 'Your projects as project lead and the people in them: booked hours in the period (the darker, the more) and members without hours. Drafts of others do not count.',
    period: 'Period',
    ranges: {
      '4w': 'Last 4 weeks',
      '3m': 'Last 3 months',
      '12m': 'Last 12 months',
    },
    searchProject: 'Search project',
    onlyHours: 'Only with hours',
    inactive: 'Also paused and finished',
    cellHours: '{{hours}} h in the period',
    cellNoHours: 'no hours in the period',
    noMember: 'not in the project team (open project)',
    legendMember: 'Member without hours',
    hint: 'Visible to the office and management (all projects) and to project leads (their projects), like the hours of the projects themselves. Colour = share of the largest cell; the number is always the hours.',
  },
  days: {
    mode: 'Kind of choice',
    modes: {
      range: 'Period',
      days: 'Single days',
    },
    prev: 'Previous month',
    next: 'Next month',
    noWorkday: 'no working day',
    chosen: '{{count}} days chosen',
    chosen_one: '{{count}} day chosen',
    clear: 'Clear choice',
    legendHoliday: 'Public holiday',
    legendAbsence: 'already entered',
    hint: 'Click working days, also across months. Weekends, public holidays and days off cannot be chosen and never count; days with only such days between them become one record.',
    records: 'is saved as {{count}} records',
    records_one: 'is saved as {{count}} record',
    ignored: '{{count}} chosen days are no working days and do not count',
    ignored_one: '{{count}} chosen day is no working day and does not count',
  },
  holidays: {
    add: 'Add holiday',
    editTitle: 'Edit holiday',
    intro: 'Every calendar is fully editable: rename, move, set to half a day or remove standard holidays (and restore them), add own days once or every year, for one calendar or for all. Movable holidays (e.g. Easter) are changed per year.',
    source: 'Source',
    sources: {
      standard: 'Standard',
      changed: 'Changed',
      custom: 'Own day',
    },
    yearly: 'every year',
    yearlyLabel: 'Every year on this day',
    yearlyHint: 'Applies every year on the same date from the chosen year on (e.g. 24.12. half day). Not suitable for movable holidays.',
    remove: 'Remove (working day)',
    removedTitle: 'Removed standard holidays',
    restore: 'Restore',
    saved: 'Holiday saved.',
    removed: '"{{name}}" removed: the day is a working day now.',
    deleted: '"{{name}}" deleted.',
    restored: '"{{name}}" restored.',
    wasName: 'Standard: {{name}}',
    moveHint: 'Moved: {{date}} is then no holiday any more.',
    calendarHintStandard: 'Standard holiday of this calendar: changes apply to this calendar.',
  },
  stepUp: {
    title: 'Additional confirmation',
    text: '"{{module}}" is protected additionally. Please confirm again; the confirmation holds for {{minutes}} minutes.',
    password: 'Password',
    totp: 'Code of the authenticator app',
    demoHint: 'Demo: confirmation word "demo".',
    confirm: 'Confirm and open',
    unavailable: 'No confirmation is possible for your account (Microsoft sign-in only, without password and authenticator app). Set up the authenticator app in the profile.',
    settingsTitle: 'Additional confirmation for billing and customers',
    forBilling: 'Open billing only after confirming again',
    forCustomers: 'Open customers only after confirming again',
    minutes: 'Confirmation holds',
    minutesValue: '{{count}} minutes',
    minutesValue_one: '{{count}} minute',
    settingsHint: 'Only people with the right of the module may change this, not the technical administration. Every confirmation and every failed attempt is logged; failed attempts lock the account like at the sign-in.',
    limits: 'Limits: the confirmation protects against others using an open session and against access through the application. Whoever has direct access to the server, the database or the backups can read the data there; only operational measures help against that (separate access, encrypted backups, logging).',
  },
  jobs: {
    intro: 'Every night the maintenance runs (Forma synchronisation, recurring entries, backup, target hours, cleanup); every step is recorded with its result. The daily check at the end looks for problems that arose in the background. Reports hold counts and project numbers, no personal data.',
    kind: 'Kind',
    kinds: {
      all: 'All reports',
      nightly: 'Nightly maintenance',
      check: 'Check (manual)',
    },
    status: {
      ok: 'fine',
      warning: 'warnings',
      error: 'errors',
      running: 'running',
      skipped: 'skipped',
    },
    started: 'Start',
    steps: 'Steps',
    step: 'Step',
    duration: 'Duration',
    findings: 'Findings',
    result: 'Result',
    failedCount: '{{count}} failed',
    noFindings: 'none',
    noFindingsLong: 'No problems found.',
    checkNow: 'Check now',
    checked: 'Check done: {{count}} findings.',
    checked_one: 'Check done: {{count}} finding.',
    exportList: 'List as Excel',
    exportOne: 'Report as Excel',
    latestProblem: 'Latest report ({{date}}): {{errors}} errors, {{warnings}} warnings.',
    open: 'Open',
    none: 'No reports yet. The first one comes with the next night or with "Check now".',
    report: 'Report',
    reportTitle: '{{kind}} of {{date}}',
    summary: '{{steps}} steps, {{errors}} errors, {{warnings}} warnings',
    lookAt: 'Look at',
    codes: {
      step_failed: {
        short: 'Step failed',
        text: '{{step}}: {{error}}',
      },
      forma_errors: {
        short: 'Forma errors',
        text: 'Synchronisation with errors in {{count}} projects: {{projects}}.',
      },
      forma_warnings: {
        short: 'Forma warnings',
        text: 'Synchronisation with warnings in {{count}} projects: {{projects}}.',
      },
      forma_lost_projects: {
        short: 'Forma connection lost',
        text: '{{count}} active projects without Forma access or deleted in Forma: {{projects}}.',
      },
      hours_on_lost_tasks: {
        short: 'Hours on deleted Forma tasks',
        text: '{{entries}} entries not billed on {{count}} tasks deleted in Forma or out of reach.',
      },
      outlook_errors: {
        short: 'Outlook appointments failed',
        text: '{{count}} appointments could not be written (check the permission of the mailbox).',
      },
      backup_missing: {
        short: 'Backup missing',
        text: 'No backup in the last 36 hours (last: {{last}}).',
      },
      months_open: {
        short: 'Months open',
        text: '{{count}} past months are not closed ({{months}}).',
      },
      approvals_waiting: {
        short: 'Approvals waiting',
        text: '{{count}} entries have been waiting for approval for more than {{days}} days.',
      },
      long_days: {
        short: 'Unusually long days',
        text: '{{count}} days with more than 16 booked hours of one person (last 60 days).',
      },
      forma_idle: {
        short: 'Forma sync idle',
        text: 'No synchronisation for more than a day (last: {{last}}).',
      },
    },
    stepNames: {
      'forma-auto-import': 'Take over new Forma projects',
      'forma-full-sync': 'Full Forma synchronisation',
      'recurring-entries': 'Recurring entries',
      snapshot: 'Backup',
      'snapshot-retention': 'Remove old backups',
      'capacity-cache': 'Compute target hours',
      'documents-retention': 'Delete documents after the period',
      'powerbi-export': 'Power BI export',
      'sessions-cleanup': 'Remove expired sessions',
      'daily-check': 'Daily check',
    },
  },
  leave: {
    bookedWarning: '{{hours}} h are already booked in this period. Is the period right? After approval the bookings count in addition.',
    cut: {
      text: 'Leave is entered on these days. Days of sickness with a doctor\'s note do not count as leave (§ 9 BUrlG; Turkey: Labour Act art. 56, Annual Paid Leave Regulation art. 6): the leave is cut by these days, {{days}} leave days go back; the leave is not extended.',
      confirm: 'Cut the leave and credit {{days}} leave days',
      self: 'Leave is entered on these days. Please report the sickness with the doctor\'s note to HR: HR enters the sickness and credits the leave days back.',
    },
    bookedInPeriod: '{{hours}} h already booked in the period',
    compInfo: 'Time off in lieu is approved like leave. The day stays part of the target hours and is not booked, which lowers the extra hours.',
    overtimeAfter: 'Extra hours afterwards: {{hours}} h',
    overtimeShort: 'Extra hours',
    overtime: {
      title: 'Extra hours and time off in lieu',
      earned: 'Balance from bookings',
      adjusted: 'Paid out / corrected',
      planned: 'Time off in lieu planned',
      requested: 'Time off in lieu requested',
      available: 'Available',
      hint: 'Since {{since}}, up to yesterday: booked hours minus target hours. Work on weekends, public holidays and absence days and hours above the target raise the balance; time off in lieu and payouts lower it. Payouts and corrections are entered by HR. A guide from the project bookings, not a working time account.',
    },
    carryoverHint: 'Untaken carry-over is not removed automatically. After the date HR adjusts it; whether it lapses depends, among other things, on whether the employee was told in time that it would lapse.',
    title: 'My absences',
    subtitle: 'Request leave and other absences, report sickness with the certificate, keep entitlement and requests in view.',
    request: 'Request absence',
    sickNote: 'Report sickness',
    requestTitle: 'Request absence',
    sickTitle: 'Report sickness',
    hrTitle: 'Enter absence',
    person: 'Person',
    kind: 'Kind',
    extent: 'Extent',
    modes: {
      full: 'Whole days',
      half: 'Half day',
      hours: 'Hours',
    },
    parts: {
      am: 'Morning',
      pm: 'Afternoon',
    },
    hours: 'Hours',
    startTime: 'Start',
    note: 'Note',
    noteHint: 'Optional, e.g. the occasion. Do not enter diagnoses.',
    preview: 'Working days: {{days}} ({{hours}} h)',
    previewHours: '{{hours}} h',
    previewDays: 'Working days: {{days}}',
    balanceAfter: 'Available afterwards: {{value}} {{unit}}',
    balanceAfterYear: 'Available afterwards ({{year}}): {{value}} {{unit}}',
    overlap: 'There is already an absence on these days.',
    needsApproval: 'The request is approved or rejected; until then it does not lower the target hours.',
    submit: 'Request',
    save: 'Enter',
    requested: 'Request sent',
    recorded: 'Entered',
    withdraw: 'Withdraw',
    cancel: 'Cancel',
    withdrawn: 'Withdrawn',
    status: {
      requested: 'Requested',
      approved: 'Approved',
      rejected: 'Change requested',
      cancelled: 'Cancelled',
    },
    balance: {
      entitlement: 'Entitlement {{year}}',
      carryover: 'Carried over',
      carryoverUntil: 'to be taken by {{date}}',
      taken: 'Taken',
      planned: 'Planned',
      requested: 'Requested',
      available: 'Available',
      none: 'No leave entitlement entered for this year yet (HR).',
    },
    units: {
      days: 'days',
      hours: 'h',
    },
    empty: 'No absences this year.',
    outlookNote: 'Approved absences appear automatically in your Outlook calendar with the status "Out of office".',
    decidedBy: 'decided by {{name}}',
    period: 'Period',
    state: 'Status',
    approve: 'Approve',
    reject: 'Request change',
    rejectTitle: 'Request a change',
    rejectReason: 'What should be changed? (the person sees the note)',
    approvedMany: '{{count}} approved',
    rejectedMany: '{{count}} change requests sent',
    undone: '{{count}} decisions taken back',
    undone_one: '{{count}} decision taken back',
    queueEmpty: 'No open requests.',
    own: 'Own request: decided by another person with the HR right',
    restAfter: 'Left afterwards',
    alsoAbsent: 'Same discipline absent: {{names}}',
    requestedAt: 'Sent',
    entitlement: 'Entitlement',
    editEntitlement: 'Edit entitlement',
    unit: 'Unit',
    amount: 'Entitlement per year',
    carryover: 'Carried over from last year',
    carryoverUntil: 'Carry-over to be taken by',
    sickDays: 'Sick (working days)',
    allTab: 'All absences',
    overviewTab: 'Entitlements and balances',
    eventsTab: 'Company events',
    holidaysTab: 'Public holidays',
    cancelTitle: 'Cancel absence',
    cancelReason: 'Reason',
    statusFilter: 'Status',
    allStatuses: 'All',
    hrInfo: 'Absences entered by HR count as approved at once.',
    importHint: 'CSV with the columns email;kind;from;to;halfDay;externalRef. Kinds: vacation, special, training, overtime, unpaid, sick, child_sick, parental, other. The same externalRef updates the record.',
    events: {
      new: 'Add event',
      name: 'Title',
      country: 'Location',
      allCountries: 'All locations',
      hint: 'In the team calendar, on the start page and – with participants – in their Outlook calendars. Days off for everybody (e.g. company closure) go under "Holidays" as a whole or half day, then they lower the target; a company event (e.g. a site visit) is working time and does not.',
      edit: 'Edit event',
      part: 'Duration',
      partHint: 'Half days and times only on a single day.',
      parts: {
        full: 'all day',
        time: 'time range',
      },
      start: 'Start',
      end: 'End',
      notePlaceholder: 'e.g. meeting point, protective equipment, contact',
      participants: 'Participants',
      participantsCount: '{{count}} participants',
      participantsCount_one: '{{count}} participant',
      everybody: 'for everybody',
      searchPerson: 'Search person',
      participantsHint: 'Without a choice the event is for everybody (of the location). With a choice the participants see it in their row of the team calendar, on the start page and – with the Outlook connection switched on – as a "busy" appointment in their Outlook calendar (no invitation).',
      deleted: 'Event "{{title}}" deleted.',
    },
    profileBirthday: 'Birthday (day and month)',
    profileShowBirthday: 'Show in the team calendar',
    profileBirthdayHint: 'Voluntary. The year is not stored.',
    decidedByHr: 'Decided by: HR.',
    decidedByName: 'Decided by: {{name}}.',
    sickInfo: 'The sick report counts at once: target hours, team calendar (only "absent") and Outlook. HR confirms it.',
    restAfterHint: 'For leave the remaining entitlement, for time off in lieu the extra hours, each after this and all other open requests of the person.',
    sickRecorded: 'Sick report recorded; HR confirms it.',
    resubmit: 'Adjust and resubmit',
    changeNote: 'Note on the change',
    changeHint: 'The days are free again. Please adjust the request and submit it again.',
    resubmitted: 'Sent again',
    noOvertimeAccount: 'No overtime account is kept for you; time off in lieu is therefore not provided.',
  },
  team: {
    leadHint: 'Highlighted: projects the person leads.',
    legendEvent: 'Company event (taking part)',
    title: 'Team',
    subtitle: 'Who is there when: absences, public holidays of the location, company events and each person\'s projects. Nobody sees hours or tasks here.',
    today: 'Today',
    until: '{{label}} until {{date}}',
    present: 'In',
    projects: 'Projects',
    noProjects: 'No project',
    lead: 'Lead',
    birthday: 'Birthday',
    events: 'Company events',
    country: 'Location',
    all: 'All',
    discipline: 'Discipline',
    search: 'Search name',
    legendAbsent: 'Absent',
    legendHoliday: 'Public holiday',
    legendFree: 'No working day',
    people: 'People',
    calendar: 'Calendar',
    mail: 'Write an e-mail',
    privacy: 'Sickness and other personal reasons appear only as "absent".',
  },
  overtime: {
    tab: 'Extra hours',
    intro: 'Extra hours per month for payroll: where the hours above the target came from, what was taken as time off in lieu and what was paid out. A payout (with the salary of a month) or a correction lowers or raises the balance at once, is logged and is visible to the person in "My absences". The basis are the project bookings; for payroll use a month only after the month is closed.',
    month: 'Month',
    closed: 'Month closed',
    open: 'Month still open: values can change',
    excel: 'Excel for payroll',
    empty: 'No employees with a working time contract in this month.',
    adjust: 'Payout or correction',
    adjustTitle: 'Extra hours: {{name}}',
    kind: 'Kind',
    kinds: {
      payout: 'Payout',
      correction: 'Correction',
    },
    hours: 'Hours',
    sign: 'Sign',
    payoutHint: 'Month of the payroll that pays the hours.',
    correctionHint: 'E.g. a balance from before Projektzeit or an agreement. A reason is required.',
    balanceNow: 'Balance today',
    balanceParts: 'bookings {{earned}} h, paid out/corrected {{adjusted}} h, time off in lieu planned {{planned}} h',
    history: 'Payouts and corrections',
    none: 'No payouts or corrections yet.',
    payrollMonth: 'Payroll {{month}}',
    removeTitle: 'Delete payout or correction',
    openEntries: '{{count}} entries not approved',
    openEntries_one: '{{count}} entry not approved',
    reset: {
      button: 'Reset balance',
      title: 'Reset balance: {{name}}',
      question: 'Are you sure? The extra hours balance of {{name}} (today {{hours}} h) is set to 0: extra hours, target/actual and the notices of missing bookings count from {{date}}.',
      confirm: 'Yes, reset balance',
      date: 'Counts from',
      dateHint: 'Usually today, or the day from which the person books completely. Not in the future.',
      notePlaceholder: 'e.g. introduction phase, started with Projektzeit on …',
      keeps: 'The booked hours and entries stay unchanged. Payouts and corrections before the day no longer count. The person gets a notification with the hours removed; the change is logged and can be taken back.',
      done: 'Balance of {{name}} reset ({{hours}} h removed).',
      undoNote: 'Reset undone',
      since: 'Tracked from {{date}}',
      countsFrom: 'Extra hours count from {{date}}.',
    },
    cols: {
      target: 'Target',
      booked: 'Booked',
      difference: 'Difference',
      daysOff: 'Days off',
      daysOffHint: 'Weekends and other days without work in the contract',
      holiday: 'Holidays',
      absenceDay: 'Absence days',
      workdayExtra: 'Above target',
      compensation: 'Time off in lieu',
      paidOut: 'Paid out',
      balance: 'Balance today',
      balanceHint: 'All months since the start of recording, after payouts and planned time off in lieu',
    },
  },
  errors: {
    ABSENCE_OVERLAP: 'There is already a requested or approved absence on these days.',
    SICK_DURING_LEAVE: 'Leave is entered on these days. HR enters a sickness during leave with the doctor\'s note; the leave days are credited back.',
    STEP_UP_REQUIRED: 'Please confirm again to open this area.',
    STEP_UP_UNAVAILABLE: 'No confirmation is possible for your account; set up the authenticator app.',
    ABSENCE_PAST_MIDNIGHT: 'An absence of some hours ends on the same day (24:00 at the latest). Please adjust the start or the duration.',
    ATTACHMENT_TYPE: 'This type of file is not accepted. Allowed are PDF, JPG, PNG, WEBP and HEIC; the content must match the type of file.',
    ATTACHMENT_SIZE: 'The file is empty or larger than 10 MB.',
    ATTACHMENT_COUNT: 'This absence has 5 documents already. Please remove one first.',
    reasons: {
      NOT_EDIT_FILE: 'The file is not an edit file of Projektzeit.',
      OTHER_DOCUMENT: 'The file belongs to another document.',
      FILE_LIMIT: 'The file is too large for an edit file (over 24 MB unpacked or more than 20,000 rows).',
      PREVIEW_CHANGED: 'The system data changed since the preview.',
      NUMBER_TAKEN: 'Another manual task of the project has this number already.',
      TASK_IN_USE: 'The task has hours in a closed month or on an invoice; it can only be hidden.',
      TASK_APPROVED_HOURS: 'The task has approved hours; only someone who may correct entries can delete it.',
      TASK_DELETED: 'The task was deleted; its entries cannot be restored.',
    },
    IMPORT_UNKNOWN_EMAIL: 'unknown e-mail {{email}}',
    IMPORT_INVALID_DATES: 'invalid dates (format YYYY-MM-DD, start not after end)',
    IMPORT_ABSENCE_OVERLAP: 'overlaps a requested or approved absence',
    IMPORT_PERIOD_LOCKED: 'falls in a closed month',
    IMPORT_SELF_APPROVAL: 'own absence: please request it, someone else decides',
    NO_WORKDAY: 'The chosen period contains no working day (weekend, public holiday or day off).',
    PROTECTED_ACCOUNT: 'Protected rights (approvals, billing, rates, HR, team figures, workload, complete export) are granted and changed only by someone who holds them. This also applies to the role, access, e-mail and activation of such accounts.',
    SELF_BILLING: 'Invoiced hours of your own entries are changed by another person with the billing right.',
    UNDO_NOT_POSSIBLE: 'Undo is no longer possible: the entry has been processed further.',
    UNAUTHENTICATED: 'Please sign in again.',
    MFA_REQUIRED: 'Second factor required.',
    FORBIDDEN: 'You do not have permission for this.',
    NOT_FOUND: 'Not found.',
    VALIDATION: 'Check your input.',
    CONFLICT: 'Changed by someone else in the meantime. Please reload.',
    PERIOD_LOCKED: 'The month is closed.',
    ENTRY_BILLED: 'Already billed. Changes only after cancelling the timesheet.',
    ENTRY_APPROVED: 'Already approved. Changes only by the project lead or office.',
    ENTRY_DELETED: 'The entry is deleted.',
    SELF_APPROVAL: 'Your own hours and your own requests are decided by someone else.',
    WRONG_STATUS: 'Not possible in this status.',
    REASON_REQUIRED: 'Please give a reason.',
    NOT_BOOKABLE: 'This task cannot be booked.',
    DESCRIPTION_REQUIRED: 'Please describe the activity.',
    FUTURE_DATE: 'Cannot book that far into the future.',
    STEP_MINUTES: 'Please record hours in the configured step.',
    LAST_ADMIN: 'The last administrator cannot be removed.',
    FORMA_ERROR: 'Forma returned an error.',
    FORMA_NOT_CONNECTED: 'Forma is not connected.',
    INTERNAL: 'Unexpected error. Please try again.',
    NETWORK: 'No connection to the server.',
    CSRF: 'Request rejected.',
    INVALID_CREDENTIALS: 'Email or password is wrong.',
    INVALID_TOTP: 'Invalid code.',
    ACCOUNT_LOCKED: 'Too many attempts. Account locked for 15 minutes.',
    SETUP_INVALID: 'The setup link is invalid or expired.',
    PASSWORD_TOO_SHORT: 'Password too short.',
    PASSWORD_TOO_WEAK: 'Password too simple.',
    PASSWORD_TOO_LONG: 'Password too long.',
    bookability: {
      PROJECT_ARCHIVED: 'The project is archived.',
      TASK_ARCHIVED: 'The task is hidden.',
      NOT_MEMBER: 'Not assigned to this project.',
      TASK_NOT_ASSIGNED: 'This task is not assigned.',
      PERSON_INACTIVE: 'The person is deactivated.',
      PROJECT_NO_FORMA: 'The project has no active Forma connection and is not released for bookings.',
    },
    shortCodeTaken: 'This code is already taken. Please choose another or use “Suggest from the name”.',
    shortCodeInvalid: 'The code consists of 2 to 6 letters or digits.',
    NO_OVERTIME_ACCOUNT: 'This person has no overtime account (extra hours are settled with the salary). Time off in lieu is therefore not possible.',
  },
  picker: {
    source: 'Source',
    sources: {
      all: 'All',
      forma: 'Forma',
      manual: 'Manual',
    },
    allProjects: 'All projects',
    counts: '{{forma}} Forma, {{manual}} manual',
    location: 'Location',
    allLocations: 'All locations',
    subLocation: 'Sub-location',
    allSubLocations: 'All sub-locations',
    noSubLocations: 'No sub-locations',
    allCategories: 'All categories',
    plan: 'Plan {{hours}} h',
    more: 'More results: narrow the filters or the search.',
  },
  cockpit: {
    title: 'Cockpit',
    subtitleAll: 'All projects at a glance. Details on the project page; planned hours and staff under Planning.',
    subtitleLead: 'The projects I lead. Details on the project page; planned hours and staff under Planning.',
    noAccess: 'The cockpit is for project leads, office and management.',
    tabs: {
      overview: 'Overview',
      projects: 'Projects',
    },
    ranges: {
      thisMonth: 'This month',
      lastMonth: 'Last month',
      year: 'This year',
      last4w: 'Last 4 weeks',
      last3m: 'Last 3 months',
      last12m: 'Last 12 months',
    },
    booked: 'Booked',
    activeProjects: 'Projects with bookings',
    persons: 'People with bookings',
    approvalsPending: 'Open approvals',
    budgetAlerts: 'Budget 80 % or more',
    overPlan: 'Tasks over plan',
    unbilled: 'Not invoiced yet (h)',
    hoursByProject: 'Hours by project',
    budgetUsedShort: 'Budget {{value}} used',
    otherProjects: '{{count}} more projects',
    otherProjects_one: '{{count}} more project',
    alerts: 'Notes',
    noAlerts: 'No projects with budget of 80 % or more or tasks over plan.',
    budgetNote: '{{booked}} of {{budget}} h',
    overPlanNote: '{{count}} tasks over plan',
    overPlanNote_one: '{{count}} task over plan',
    weekly: 'Weekly hours of all projects',
    sort: 'Sort',
    sorts: {
      period: 'Hours in period',
      total: 'Total hours',
      budget: 'Budget used',
      overPlan: 'Tasks over plan',
      number: 'Project number',
    },
    colPeriod: 'Period (h)',
    colTotal: 'Total (h)',
    colPlanTasks: 'Tasks with plan',
    colDone: 'Finished: plan / actual',
    colDoneHint: 'Finished tasks with a plan: sum of plan / sum booked',
    colInvoiced: 'Invoiced / recorded',
    overPlanShort: '{{count}} over plan',
    projectsHint: 'Budget and plan values cover the whole project, hours the selected period. Invoiced / recorded: invoiced hours compared with the recorded hours of the same entries.',
    teamEfficiencyHint: 'Planned share / booked hours of all evaluated tasks',
    weekFlags: 'Weeks below / above target',
    weekFlagsHint: 'Deviation above {{hours}} h per week',
    weekFlagsCol: 'Week flags',
    noFlags: 'normal ({{count}} weeks)',
    noFlags_one: 'normal ({{count}} week)',
    tasksCount: '{{count}} tasks',
    tasksCount_one: '{{count}} task',
    personTitle: 'Key figures: {{name}}',
    saved: 'Against plan',
    savedHint: 'Planned share minus booked hours (positive = faster)',
    toAnalysis: 'Project analysis',
    searchProject: 'Search project (number or name)',
  },
  projectKpi: {
    tasksWithPlan: 'Tasks with plan',
    planHours: 'Plan {{plan}} h, booked {{booked}} h',
    done: 'Finished tasks: plan / actual',
    doneHint: '{{count}} tasks: plan {{plan}} h, booked {{booked}} h',
    doneHint_one: '{{count}} task: plan {{plan}} h, booked {{booked}} h',
    overPlan: 'Open tasks over plan',
    invoicedFactor: 'Invoiced / recorded',
    invoicedHint: '{{invoiced}} h invoiced for {{recorded}} h recorded',
    noInvoiced: 'Nothing invoiced yet.',
    overPlanList: 'Open tasks over plan',
    noOverPlan: 'No open task over plan.',
    plan: 'Plan (h)',
    onlyOverPlan: 'Only over plan',
    notEvaluated: 'not evaluated',
    planHoursLabel: 'Plan (hours)',
  },
  categories: {
    hint: 'Categories for manual tasks (M numbers). The group "Internal" is overhead and never invoiced. Deactivate categories that are no longer needed instead of deleting them, so existing tasks keep their category.',
    new: 'New category',
    edit: 'Edit category',
    createInternal: 'Create internal tasks',
    internalCreated: '{{count}} tasks created in project {{project}}',
    internalCreated_one: '{{count}} task created in project {{project}}',
    internalNone: 'All internal categories already have a task.',
    translations: 'English · Turkish',
    tasks: 'Tasks',
    fixed: 'fixed',
    nameDe: 'Name (German)',
    nameEn: 'Name (English)',
    nameTr: 'Name (Turkish)',
    group: 'Group',
    groups: {
      projekt: 'Project related',
      intern: 'Internal (overhead)',
    },
    sort: 'Order',
    fixedLabel: 'Fix the billing type',
    fixedHint: 'Bookings cannot change the billing type.',
    activeLabel: 'Active',
    activeHint: 'Inactive categories cannot be chosen for new tasks.',
    noOvertime: 'no extra hours',
    noOvertimeLabel: 'Does not count as extra hours',
    noOvertimeHint: 'Hours on tasks of this category do not count for the overtime account (e.g. voluntary training). Applies only when the setting is switched on.',
    noOvertimeRetro: 'The change also applies to hours already booked in this category and thus changes overtime balances.',
  },
  analysis: {
    title: 'Project analysis',
    subtitle: 'State of all projects: progress, schedule and hours against plan. Open a project for buildings, floors, trades, schedule and team.',
    backToPortfolio: 'Back to all projects',
    period: 'Period',
    periodHint: 'The period applies to hours and costs. Progress and schedule always show the state of today.',
    periods: {
      all: 'Whole project',
      year: 'This year',
      last12w: 'Last 12 weeks',
      last4w: 'Last 4 weeks',
      month: 'This month',
      lastMonth: 'Last month',
      custom: 'Custom period',
    },
    includeInactive: 'Show paused and completed projects',
    excel: 'Excel',
    pdf: 'PDF report',
    noProjects: 'No projects in your access.',
    search: 'Search project, number or client',
    shownOf: '{{shown}} of {{total}} projects',
    more: 'Show more',
    overdueAll: 'Overdue tasks of all projects',
    noOverdue: 'No overdue tasks.',
    dueOn: 'due {{date}}',
    daysLate: '{{count}} days overdue',
    daysLate_one: '{{count}} day overdue',
    definitions: 'Progress: finished (closed) tasks / all tasks, or by planned hours (setting per project). Schedule: late when at least 3 or a fifth of the open tasks with a due date are overdue; at risk with one overdue task or a task due soon that has not started. Plan/actual ratio: planned hours of the finished tasks / their booked hours (above 100 % = faster than planned). Hidden tasks do not count, their hours do.',
    portfolioReport: 'Project portfolio',
    projectReport: 'Project report',
    reportAsOf: 'As of {{date}}',
    printNow: 'Print / save as PDF',
    demoPrint: 'In the demo the print window may be blocked. In the installed platform "PDF report" opens the print dialog directly; choose "Save as PDF" and landscape there.',
    kpi: {
      projects: 'Projects',
      progress: 'Progress',
      doneOf: '{{done}} of {{tasks}} tasks finished',
      byPlan: '{{done}} of {{plan}} planned hours finished',
      overdue: 'Overdue',
      dueSoon: '{{count}} due within 7 days',
      overdueSoon: '{{overdue}} overdue, {{soon}} due within 7 days',
      withDue: 'of {{count}} open tasks with a due date',
      actual: 'Hours in period',
      lifetime: 'total {{hours}} h',
      lifetimeShort: 'Actual total (h)',
      plan: 'Planned hours',
      plannedTasks: '{{count}} of {{tasks}} tasks with a plan',
      planUse: 'Plan used',
      planUseHint: 'Booked / planned (tasks with a plan)',
      planRatio: 'Plan/actual ratio',
      planRatioHint: 'Finished tasks: plan / actual',
      onTime: 'On time',
      avgClose: 'avg. {{days}} days to finish',
      noneDone: 'nothing finished yet',
      tasks: 'Tasks',
      cost: 'Cost',
      costHint: 'Booked hours × cost rate',
      costMissing: 'A cost rate is missing for {{hours}} h.',
      sales: 'Sales value',
      salesHint: 'Billable hours × sales rate',
      margin: 'Contribution',
      marginHint: 'Sales value minus cost',
      fee: 'Fee per contract',
      feeHint: 'Fixed fee of the project',
    },
    schedule: {
      label: 'Schedule',
      ok: 'on track',
      risk: 'at risk',
      late: 'late',
      none: 'no due dates',
    },
    scheduleHint: {
      ok: 'No overdue tasks, none due soon without start.',
      risk: 'Single tasks overdue, or due soon and not started.',
      late: 'Several tasks overdue (at least 3 or a fifth of the open ones with a due date).',
      none: 'No open tasks with a due date.',
    },
    buildings: 'Buildings and floors',
    buildingAria: 'Schematic view of the buildings with the progress per floor',
    settings: 'Settings',
    filters: 'Filters',
    removeFilter: 'Remove filter',
    clearFilters: 'Reset all filters',
    tabs: {
      overview: 'Overview',
      building: 'Buildings',
      matrix: 'Matrix',
      schedule: 'Schedule',
      team: 'Team',
      tasks: 'Tasks',
      cost: 'Costs',
    },
    dims: {
      building: 'Building',
      floor: 'Floor',
      trade: 'Trade',
      phase: 'Work stage',
      category: 'Category (Forma)',
      type: 'Type (Forma)',
      stage: 'Stage',
      person: 'Person',
    },
    none: 'not set',
    stages: {
      open: 'open',
      progress: 'in progress',
      review: 'in review',
      done: 'finished',
    },
    legendProgress: 'Progress:',
    legendHours: 'Hours:',
    legendLess: 'few',
    legendMore: 'many',
    export: {
      button: 'Export …',
      title: 'Export project analysis',
      format: 'Format',
      parts: 'Content',
      formats: {
        pdf: 'PDF (print the report)',
        html: 'Interactive HTML file',
        xlsx: 'Excel (data)',
      },
      formatHints: {
        pdf: 'To file and send; use "Save as PDF" in the print dialog.',
        html: 'One file that any browser opens, without signing in: whoever receives it sees the details when pointing at a storey, a bar or a cell.',
        xlsx: 'Tables to calculate with; without drawings (building, schedule and costs only in PDF and HTML).',
      },
      part: {
        overview: 'Key figures and progress',
        building: 'Buildings',
        matrix: 'Floor × trade',
        schedule: 'Schedule (burn-up, bar chart, overdue)',
        team: 'Who works where',
        tasks: 'All tasks',
        cost: 'Costs and sales value',
      },
      notAvailable: 'Not available for this project',
      notInExcel: 'Only in PDF and HTML',
      filtersHint: 'The period and the filters of the analysis apply to the export as well. The file holds only what you see with your rights.',
      go: {
        pdf: 'Print report',
        html: 'Create HTML file',
        xlsx: 'Create Excel file',
      },
      html: 'HTML (interactive)',
      htmlHint: 'Report as a file that any browser opens; details when pointing at the values',
      htmlNote: 'Created on {{date}} by {{name}} with {{app}}. Point at storeys, bars and cells to see the details.',
    },
    noTasks: 'no tasks',
    floorAria: '{{progress}} finished ({{done}} of {{tasks}} tasks), {{overdue}} overdue, {{hours}} h in the period',
    tipProgress: '{{progress}} finished ({{done}} of {{tasks}})',
    tipStages: 'in progress {{progress}} · in review {{review}} · open {{open}}',
    tipDue: '{{overdue}} overdue · {{soon}} due within 7 days',
    tipHours: '{{actual}} h booked in the period · plan {{plan}} h',
    wholeProject: 'Whole project',
    buildingTotal: '{{progress}} finished · {{done}}/{{tasks}} tasks',
    general: 'Building-wide',
    area: 'Area without floors',
    progressBy: 'Progress by {{dim}}',
    noFloorsTitle: 'No building picture yet',
    noFloors: 'No floors found. Under "Settings" choose the Forma field or the level of the locations the floor comes from. Fields are taken over in the "Forma" tab of the project.',
    burn: {
      title: 'Course (burn-up)',
      total: 'Tasks in total',
      due: 'due by date',
      finished: 'finished',
      aria: 'Burn-up: {{finished}} of {{total}} tasks finished',
      week: 'In the week: {{done}} finished, {{created}} new, {{hours}} h booked',
    },
    hoursPerWeek: 'Hours per week',
    overdueTop: 'Overdue the longest',
    colourBy: 'Colour by',
    modes: {
      progress: 'Progress',
      schedule: 'Schedule',
      hours: 'Hours',
    },
    buildingHint: 'Click a floor to filter the whole analysis to it, click again to remove the filter. The picture is drawn from the floors of the tasks: basements below the ground line, the roof on top, building-wide tasks on the sign below.',
    detailOf: '{{place}}: progress by {{dim}}',
    rows: 'Rows',
    cols: 'Columns',
    matrixHint: 'Progress on top, finished/total below; red number: overdue tasks. Click a cell, row or column to filter the analysis.',
    noData: 'No data for this selection.',
    groupBy: 'Group by',
    openOnly: 'Open tasks only',
    gantt: {
      empty: 'No tasks with a start or due date.',
      overdue: 'overdue',
      noDue: 'no due date',
      overdueDays: '{{count}} days overdue',
      overdueDays_one: '{{count}} day overdue',
      limited: 'The first {{count}} tasks are shown; narrow down with the filters.',
      without: '{{count}} tasks without dates are not shown.',
      without_one: '1 task without dates is not shown.',
    },
    overdueAllProject: 'Overdue tasks ({{count}})',
    overdueCount: '{{count}} overdue',
    whoWhere: 'Who works on what (hours in the period)',
    teamHint: 'Hours as booked in Projektzeit in the chosen period.',
    personTasks: 'People: hours and the tasks they booked on',
    noHours: 'No hours in this period.',
    hoursUnit: 'Values in hours.',
    sortBy: 'Sort by',
    sort: {
      overdue: 'Overdue first',
      due: 'Due date',
      ref: 'Number',
      plan: 'Planned hours',
      actual: 'Actual hours',
    },
    startDue: 'Start - due',
    showMore: 'Show {{count}} more',
    costNote: 'Cost from the cost rates of the people on the booking day; sales value from the sales rates of the billable hours (invoiced hours where recorded). Visible only with the respective right.',
    cfg: {
      intro: 'Where the analysis takes building, floor, trade and work stage of a task from. The suggestion comes from the Forma fields taken over and the locations and can be changed here.',
      none: 'not used',
      field: 'Field: {{title}}',
      loc1: 'Forma location, level 1',
      loc2: 'Forma location, level 2',
      noFields: 'No Forma fields are taken over for this project yet (project, tab "Forma", fields).',
      buildingHint: 'e.g. sub-project, building part, house',
      floorHint: 'Names such as 2.UG, KG, EG, 1.OG, DG, roof are recognised and sorted from the bottom up.',
      progressBy: 'Measure progress by',
      progressByHint: 'By planned hours only tasks with a plan count; large tasks weigh more.',
      byCount: 'Number of tasks',
      byPlan: 'Planned hours',
      fieldsHint: 'Further fields taken over (e.g. priority, sprints) are available as filters automatically.',
      reset: 'Use suggestion',
    },
    scheduleCol: 'Schedule',
    view: {
      label: 'View',
      cards: 'Cards',
      list: 'List',
    },
  },
  documents: {
    title: 'Documents',
    column: 'Document',
    choose: 'Choose file',
    pickHint: 'PDF or photo (JPG, PNG, HEIC), at most 10 MB, up to 5 files. Drag and drop works too.',
    tooMany: 'At most {{count}} files per absence.',
    badType: '"{{name}}": only PDF or pictures (JPG, PNG, WEBP, HEIC).',
    tooLarge: '"{{name}}" is larger than 10 MB.',
    download: 'Download',
    view: 'View',
    heicHint: 'Not every browser shows HEIC photos (iPhone). Download to view.',
    demoPdf: 'In the demo the preview shows the text of the document; in the installed platform the PDF opens in the browser.',
    noPreview: 'There is no preview for this file. Please download it.',
    added: 'Document added',
    missing: 'A document is expected for this period; none has been added yet.',
    missingShort: 'Document missing',
    none: 'No documents.',
    upload: 'Upload {{count}} files',
    upload_one: 'Upload file',
    removeTitle: 'Delete document',
    removeText: 'The content of the file is deleted; name and time stay in the log. After the decision only HR can delete, with a reason.',
    removed: 'Document deleted',
    count: '{{count}} documents',
    count_one: '1 document',
    add: 'Add document',
    uploadFailed: '{{count}} files could not be uploaded; please add them under "My absences".',
    uploadFailed_one: 'One file could not be uploaded; please add it under "My absences".',
    labelRequired: 'Document (required)',
    labelOptional: 'Document (optional)',
    healthHint: 'Sick note or medical certificate, required from calendar day {{day}}. Only you and HR can see it.',
    otherHint: 'e.g. invitation, registration, certificate. You, the approver and HR can see it.',
    requiredNotice: 'A certificate is expected from calendar day {{day}}. You can also upload it later under "My absences".',
    missingNotice: 'Certificate missing for {{count}} sick reports:',
    missingNotice_one: 'Certificate missing for one sick report:',
  },
  leaveRequests: {
    title: 'Absence requests',
    noAccess: 'You do not decide absence requests. HR sets who approves the requests of a person.',
    subtitleHr: 'All open requests and sick reports. You can also decide requests of people with their own approver (e.g. team lead); only HR confirms sick reports.',
    subtitleApprover: 'Requests of the people you approve for (leave, time off in lieu, training, special leave). HR confirms sick reports.',
    open: 'Open',
    decided: 'Recently decided',
    provisionalHint: 'Sick reports count already while they wait for confirmation.',
    healthNote: 'Health data: HR only',
    approverIs: 'approver: {{name}}',
    approverHr: 'approver: HR',
    confirm: 'Confirm',
    decidedHint: 'Your decisions of the last 30 days. "Undo" works while nothing changed afterwards and the month is open.',
    noneDecided: 'No decisions in the last 30 days.',
  },
  customers: {
    title: 'Customers',
    subtitle: 'Contact list, companies and payment terms of the projects. No accounting, no invoicing.',
    noAccess: 'For management only (right "Customer portfolio").',
    tabs: {
      contacts: 'Contacts',
      companies: 'Companies',
      terms: 'Terms',
      migration: 'Take over from text',
    },
    search: 'Search: company, name, city, e-mail …',
    filterStatus: 'Filter by status',
    allStatus: 'All',
    showInactive: 'Include inactive contacts',
    showArchived: 'Show archived',
    archivedPill: 'archived',
    inactivePill: 'inactive',
    shownOf: '{{shown}} of {{total}} shown',
    noMatches: 'No matches for this search or status.',
    resetFilters: 'Reset filters',
    newContact: 'New contact',
    newCompany: 'New company',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    emptyContacts: 'No contacts yet. Add a contact or import your existing list as a CSV file (Excel: "Save as" → CSV).',
    emptyCompanies: 'No companies yet. Companies are created with a contact or here.',
    dncHint: 'Do not contact: please do not e-mail or call.',
    statuses: {
      prospect: 'Prospect',
      offered: 'Offer submitted',
      active: 'Active customer',
      former: 'Former customer',
      partner: 'Business partner',
      do_not_contact: 'Do not contact',
    },
    cols: {
      company: 'Company',
      contact: 'Contact',
      email: 'E-mail',
      phone: 'Phone',
      status: 'Status',
      note: 'Note',
      city: 'City',
      owner: 'Responsible',
      contacts: 'Contacts',
      projects: 'Projects',
      hours12m: 'Hours (12 mo.)',
      wip: 'Unbilled',
      billed: 'Documents net',
      project: 'Project',
      role: 'Role',
      roles: 'Responsible for',
      kind: 'Kind',
      client: 'Client',
      recipient: 'Invoice recipient',
      paymentTerm: 'Payment term',
      method: 'Billing',
      completeness: 'Completeness',
      items: 'Supplements',
      number: 'Number',
      period: 'Period',
      amount: 'Amount',
    },
    stats: {
      companies: 'Companies',
      active: 'Active customers',
      prospects: 'Prospects and offers',
      prospectsHint: 'Status "Prospect" or "Offer submitted"',
      wip: 'Unbilled work',
      wipHint: 'Approved hours not yet billed, times the rate of the booking day',
      billed: 'Documents (net)',
      billedHint: 'Finalised Stundennachweis and Abschlag documents; not accounting',
    },
    types: {
      company: 'Company',
      public: 'Public body',
      person: 'Private person',
    },
    roles: {
      auftraggeber: 'Client (Auftraggeber)',
      rechnungsempfaenger: 'Invoice recipient',
      bauherr: 'Owner / end client',
      generalplaner: 'General planner / partner',
      subplaner: 'Sub-planner',
    },
    contactRoles: {
      invoice: 'Invoice',
      technical: 'Technical',
      sn_approver: 'Timesheet approval',
    },
    methods: {
      hourly: 'Time and material (hours)',
      hoai: 'HOAI instalments',
      fixed_fee: 'Fixed fee',
      mixed: 'Mixed',
    },
    channels: {
      email: 'E-mail',
      post: 'Post',
      portal: 'Portal',
    },
    itemKinds: {
      nachtrag: 'Supplement (Nachtrag)',
      nebenkosten: 'Ancillary costs',
    },
    itemStatuses: {
      offered: 'Offered',
      approved: 'Approved',
      rejected: 'Rejected',
      invoiced: 'Billed',
    },
    taxScenarios: {
      de_standard: 'Domestic, standard rate (from settings)',
      pending_advisor: 'Tax advisor confirmation pending',
    },
    company: {
      newTitle: 'New company',
      editTitle: 'Edit company',
      name: 'Company name',
      status: 'Status',
      owner: 'Responsible',
      ownerHint: 'Who is in charge of this customer on our side',
      noOwner: 'not assigned',
      street: 'Street and number',
      postalCode: 'Postal code',
      city: 'City',
      country: 'Country',
      countryHint: '2 letters, e.g. DE',
      note: 'Note',
      more: 'More details for billing',
      moreHint: 'Only needed if this company receives invoices: tax number, customer number …',
      type: 'Type',
      legalName: 'Full legal name',
      vatId: 'VAT ID / tax number',
      vatIdHint: 'DE: "DE" and 9 digits; TR: 10 or 11 digits',
      taxBad: 'Check format',
      taxOk: 'Format OK',
      leitwegId: 'Leitweg-ID',
      leitwegHint: 'Required for public clients (buyer reference)',
      customerNo: 'Customer number',
      debitorNo: 'Debtor number',
      debitorHint: 'assigned by the tax advisor',
    },
    contact: {
      newTitle: 'New contact',
      editTitle: 'Edit contact',
      company: 'Company',
      pickCompany: 'Choose company …',
      newCompany: 'New company',
      firstName: 'First name',
      lastName: 'Last name',
      position: 'Position',
      phone: 'Phone',
      email: 'E-mail',
      note: 'Note',
      responsible: 'Responsible for',
      active: 'Active',
      activeHint: 'Inactive contacts (e.g. left the company) stay stored but only show in the list on request.',
      deleteTitle: 'Delete contact?',
      deleteText: 'The contact is removed from the project roles. Store business data only, no private data (GDPR).',
    },
    party: {
      add: 'Assign to project',
      title: 'Role in the project',
      none: 'Not assigned to a project yet.',
      pickProject: 'Choose project …',
      noContact: 'no specific contact',
      contactHint: 'Optional: who at this company is in charge of the project',
      remove: 'Remove role',
      removeTitle: 'Remove role?',
      removeText: 'The link between company and project is removed. The free text in the project stays unchanged.',
    },
    detail: {
      back: 'Back to customers',
      master: 'Master data',
      address: 'Address',
      contacts: 'Contacts',
      addContact: 'Add contact',
      noContacts: 'No contacts yet.',
      projects: 'Projects and roles',
      terms: 'Terms',
      archive: 'Archive',
      unarchive: 'Restore',
      archiveTitle: 'Archive company?',
      archiveText: 'The company and its contacts disappear from the lists; the roles in the projects stay. You can restore the company at any time.',
    },
    docs: {
      title: 'Timesheets and instalment documents',
      hint: 'Existing documents of this company\'s projects (read only). No invoices are created here.',
      none: 'No documents yet.',
      draft: 'Draft',
    },
    terms: {
      title: 'Terms',
      intro: 'Per project: payment term (e.g. 14 days), discount (e.g. 1.5% if paid within 7 days), deductions after VAT, invoice recipient and delivery. Click a project to edit its terms. No invoice is created here.',
      search: 'Search: project, client …',
      onlyOpen: 'Only incomplete',
      allComplete: 'All projects have complete terms.',
      none: 'no terms',
      complete: 'complete',
      gapsCount: '{{count}} open',
      gapsCount_one: '{{count}} open',
      stillNeeded: 'Still needed',
      freeText: 'Free text in the project',
      customer: 'Customer',
      partner: 'Partner',
      sectionRecipient: 'Invoice recipient and references',
      recipient: 'Invoice recipient (company)',
      noRecipient: 'not chosen',
      recipientAddress: 'Different billing address',
      recipientAddressHint: 'empty = address of the company',
      orderRef: 'Client reference (order no.)',
      buyerRef: 'Buyer reference (BT-10 / Leitweg-ID)',
      sectionPayment: 'Payment terms',
      paymentIntro: 'Payment term, discount for early payment and deductions taken from the total after VAT (e.g. rebate, retention).',
      paymentDays: 'Payment term (days)',
      paymentHint: 'Default from the settings: {{days}} days',
      discountPercent: 'Early-payment discount (%)',
      discountDays: 'if paid within (days)',
      adjustments: 'Deductions after VAT',
      adjustmentsHint: 'Each deduction refers to the total including VAT, not to the other deductions. The discount is calculated on the remaining payable amount.',
      addAdjustment: 'Add deduction',
      adjustmentLabel: 'Label (e.g. rebate)',
      modePercent: '% of the total',
      modeAmount: 'Amount (EUR)',
      exampleTitle: 'Example calculation',
      exampleNet: 'Net amount (EUR)',
      net: 'Net',
      vat: 'VAT {{percent}}%',
      gross: 'Gross (incl. VAT)',
      payable: 'Amount payable',
      payableDue: 'Amount payable within {{days}} days',
      payableDiscount: 'If paid within {{days}} days ({{percent}}% discount)',
      documentText: 'Text on the document',
      taxAdvisorNote: 'Whether and how VAT is corrected for a discount is decided by the tax advisor (§ 17 UStG). The example only shows what the customer pays and does not replace an invoice.',
      daysShort: '{{count}} days',
      daysShort_one: '{{count}} day',
      discountShort: '{{percent}}% discount within {{days}} days',
      sectionBilling: 'Billing and tax',
      method: 'Billing method',
      notChosen: 'not chosen',
      taxScenario: 'Tax scenario',
      taxOpen: 'Open question: the approved list of tax scenarios (domestic, reverse charge, EU, third country) is set by the tax advisor. Until then there is only this example, no free VAT rate.',
      sectionDelivery: 'Delivery and approval',
      delivery: 'Delivery',
      deliveryAddress: 'Delivery address (e-mail or postal)',
      snApproval: 'Timesheet needs client approval',
      snTemplate: 'Timesheet template',
      note: 'Note',
      itemsTitle: 'Supplements and ancillary costs',
      itemsTotal: 'Approved or billed',
      addItem: 'Add item',
      itemEdit: 'Edit item',
      itemTitle: 'Description',
      itemAmount: 'Amount net (EUR)',
      noItems: 'No items',
      needTerms: 'Save the terms first, then add items.',
      discountMax: 'At most {{max}} %',
    },
    gaps: {
      recipient: 'Invoice recipient missing',
      address: 'Billing address missing',
      payment_days: 'Payment term missing',
      discount: 'Discount: period missing or not shorter than the payment term',
      billing_method: 'Billing method missing',
      tax_scenario: 'Tax scenario missing',
      delivery: 'Delivery incomplete',
      vat_id: 'VAT ID missing or invalid',
      leitweg: 'Leitweg-ID missing (public client)',
    },
    import: {
      title: 'Import contact list',
      intro: 'Upload your existing table as a CSV file (Excel: Save as → CSV). You see a preview first; nothing is saved before you confirm. Existing companies stay unchanged, only new contacts are added.',
      choose: 'Choose CSV file',
      formatTitle: 'Which columns are recognised?',
      format: 'First row = headings. Recognised: Company (required), Address, Postal code, City, Country, First name, Last name, Position, E-mail, Phone, Status, Note – in German, English or Turkish. Separator ";" or ",". The status is read from the text (e.g. "Active customer", "aktif müşteri"); without a value "Prospect" applies.',
      line: 'Line',
      result: 'Result',
      rows: '{{count}} rows',
      rows_one: '1 row',
      newCompanies: '{{count}} new companies',
      newCompanies_one: '1 new company',
      newContacts: '{{count}} new contacts',
      newContacts_one: '1 new contact',
      skipped: '{{count}} already there',
      skipped_one: '1 already there',
      errors: '{{count}} with errors',
      errors_one: '1 with error',
      apply: 'Import',
      done: '{{contacts}} contacts and {{companies}} companies imported.',
      actions: {
        create_company: 'New company',
        add_contact: 'New contact',
        skip_duplicate: 'Already there',
        error: 'Error',
      },
      errorCodes: {
        missing_company: 'Company missing',
        missing_name: 'Name missing',
        unknown_status: 'Unknown status',
      },
    },
    migration: {
      intro: 'Company candidates are built from "customer" and "partner" of the projects. Spellings are only suggested as groups; you confirm each group. An equal name is no reason for an automatic merge. The free texts in the project stay as a backup.',
      none: 'No open free texts: all projects have their companies.',
      selectAll: 'Select all',
      selectNone: 'Clear selection',
      selected: '{{count}} groups selected',
      selected_one: '1 group selected',
      fieldCustomer: 'Customer → client',
      fieldPartner: 'Partner → general planner',
      projectCount: '{{count}} projects',
      projectCount_one: '1 project',
      spellings: 'Spellings',
      usedIn: 'Used in',
      createNew: 'Create new company',
      linkExisting: 'Assign to existing company',
      suggested: 'Suggestion: existing company',
      shortName: 'Company name',
      pickCompany: 'Choose company …',
      apply: 'Take over selected',
      applied: '{{created}} companies created, {{linked}} project roles set',
    },
  },
  shell: {
    back: 'Back',
    forward: 'Forward',
    backTitle: 'To the previous page (Alt + left arrow)',
    forwardTitle: 'To the next page (Alt + right arrow)',
    quickLinks: 'Company links',
    links: {
      standards: 'Standards & templates',
      meetings: 'Coordination notes',
      website: 'Website',
    },
    linkHints: {
      standards: 'Guidelines, templates and layouts of the company',
      meetings: 'Notes of the weekly coordination meeting (SharePoint)',
      website: 'Website of the company',
    },
  },
  notif: {
    title: 'Notifications',
    bellUnread: '{{count}} unread notifications',
    bellUnread_one: '1 unread notification',
    tabFeed: 'Notifications',
    tabHistory: 'My history',
    filterTitle: 'Show',
    filterReset: 'Show all',
    filterHint: 'Your choice is saved in your account and applies on every device. What you see here only concerns you and depends on your rights.',
    hideCategory: 'Hide “{{name}}”',
    showCategory: 'Show “{{name}}”',
    onlyUnread: 'Unread only',
    markAll: 'Mark all as read',
    empty: 'No notifications. Anything that needs your attention will show up here.',
    emptyFiltered: 'Nothing to show. Check the selection above.',
    unread: 'unread',
    critical: 'Urgent',
    todo: 'To do',
    categories: {
      hours: 'My hours',
      leave: 'Absences',
      approvals: 'Approvals',
      period: 'Month closing',
      missing: 'Missing entries',
      announcement: 'Announcements',
      system: 'System',
      forma: 'Forma',
    },
    historyHint: 'Your own recent actions. For other people’s data only the kind of action is shown, not the content.',
    historyEmpty: 'No actions yet.',
    historyMore: 'Load more',
    actions: {
      time_update: 'Time entry changed',
      time_delete: 'Time entry deleted',
      time_restore: 'Time entry restored',
      time_submit: 'Hours submitted',
      time_withdraw: 'Hours withdrawn',
      time_approve: 'Hours approved',
      time_approve_undo: 'Approval undone',
      time_reject: 'Hours rejected',
      time_reject_undo: 'Rejection undone',
      time_reopen: 'Hours reopened',
      time_create_for_other: 'Time booked for another person',
      time_billed: 'Hours marked as billed',
      absence_request: 'Absence requested',
      absence_create: 'Absence entered',
      absence_withdraw: 'Request withdrawn',
      absence_withdraw_undo: 'Withdrawal undone',
      absence_cancel: 'Absence cancelled',
      absence_approve: 'Absence approved',
      absence_reject: 'Absence rejected',
      absence_change_request: 'Change requested',
      absence_decision_undo: 'Decision undone',
      absence_document_add: 'Document uploaded',
      absence_document_removed: 'Document removed',
      absence_import: 'Absences imported',
      person_update: 'Person record changed',
      person_avatar: 'Profile picture changed',
      person_avatar_removed: 'Profile picture removed',
      person_create: 'Person created',
      person_activate: 'Person activated',
      person_deactivate: 'Person deactivated',
      person_role: 'Role changed',
      person_approver: 'Approver changed',
      person_reset_access: 'Access reset',
      person_fill_codes: 'Short codes filled in',
      project_create: 'Project created',
      project_update: 'Project changed',
      project_archive: 'Project archived',
      project_unarchive: 'Project restored',
      project_member_set: 'Project team changed',
      project_member_remove: 'Person removed from project',
      project_party_set: 'Company assigned to project',
      project_party_remove: 'Company assignment removed',
      task_create_manual: 'Task created',
      task_create_internal: 'Internal task created',
      task_update_manual: 'Task changed',
      task_archive: 'Task archived',
      task_unarchive: 'Task restored',
      task_plan: 'Plan hours changed',
      billing_create: 'Document created',
      billing_finalize: 'Document finalised',
      billing_cancel: 'Document cancelled',
      billing_delete_draft: 'Draft deleted',
      company_create: 'Company created',
      company_update: 'Company changed',
      company_archive: 'Company archived',
      company_unarchive: 'Company restored',
      company_contact_create: 'Contact created',
      company_contact_update: 'Contact changed',
      company_contact_delete: 'Contact deleted',
      company_contacts_export: 'Contact list exported',
      company_contacts_import: 'Contact list imported',
      terms_create: 'Terms created',
      terms_update: 'Terms changed',
      terms_item_create: 'Supplement recorded',
      terms_item_update: 'Supplement changed',
      terms_item_delete: 'Supplement deleted',
      announcement_create: 'Announcement published',
      announcement_update: 'Announcement changed',
      announcement_archive: 'Announcement archived',
      announcement_unarchive: 'Announcement restored',
      period_lock: 'Month closed',
      period_unlock: 'Month reopened',
      settings_update: 'Settings changed',
      settings_logo: 'Logo changed',
      settings_logo_removed: 'Logo removed',
      quick_favorites: 'Favourites changed',
      quick_templates: 'Week templates changed',
      notifications: 'Notification filter changed',
      contract_set: 'Contract changed',
      rate_cost: 'Cost rate changed',
      rate_sales: 'Sales rate changed',
      rate_delete: 'Rate deleted',
      company_event_create: 'Company event created',
      company_event_update: 'Company event changed',
      company_event_delete: 'Company event deleted',
      holiday_set: 'Holiday changed',
      holiday_delete: 'Holiday removed',
      absence_document_viewed: 'Document opened',
      absence_export: 'Absences exported',
      absence_leave_cut: 'Leave cut because of sickness',
      absence_resubmit: 'Request adjusted and sent again',
      allocation_fill: 'Plan filled in',
      analysis_export: 'Project analysis exported',
      analysis_export_portfolio: 'Portfolio analysis exported',
      auth_entra_linked: 'Microsoft sign-in linked',
      auth_entra_mismatch: 'Microsoft sign-in refused',
      auth_step_up: 'Confirmed again (billing, customers)',
      auth_step_up_failed: 'Additional confirmation failed',
      bi_export: 'Power BI export created',
      billing_basis_checked: 'Document marked as checked',
      billing_import_edit: 'Changes from Excel applied',
      category_create: 'Category created',
      category_update: 'Category changed',
      contract_delete: 'Contract period deleted',
      data_export_all: 'Complete export created',
      entitlement_set: 'Leave entitlement changed',
      forma_connect: 'Forma connected',
      holiday_revert: 'Holiday change undone',
      jobs_check: 'Daily check run',
      kpi_person_viewed: 'Figures of a person opened',
      kpi_team_viewed: 'Staff review opened',
      outlook_sync: 'Outlook sync run',
      overtime_remove: 'Payout or correction deleted',
      overtime_report_viewed: 'Extra hours report opened',
      overtime_reset: 'Extra hours balance reset',
      project_analysis_config: 'Project analysis settings changed',
      project_attribute_scope: 'Field values of the project limited',
      project_colors: 'Project colours assigned',
      project_fee_phases: 'Fee phases changed',
      project_forma_auto_import: 'Forma project taken over automatically',
      project_forma_import: 'Forma projects taken over',
      project_forma_link: 'Linked to Forma',
      project_forma_renamed: 'Name taken over from Forma',
      project_forma_unlink: 'Forma link removed',
      project_import_cleanup: 'Excluded tasks cleaned up',
      project_import_filter: 'Import filter changed',
      project_member_tasks: 'Tasks of a member restricted',
      project_sync_config: 'Forma settings of the project changed',
      settings_step_up: 'Additional confirmation changed',
      task_delete_manual: 'Task deleted',
      task_forma_access_lost: 'Forma task no longer reachable',
      task_forma_deleted: 'Forma task deleted',
      task_forma_restored: 'Forma task active again',
      task_template_delete: 'Task template deleted',
      task_template_save: 'Task template saved',
    },
    fields: {
      minutes: 'Duration (min.)',
      status: 'Status',
      work_date: 'Date',
      description: 'Description',
      billing_type: 'Billing type',
      work_location: 'Work location',
      task_id: 'Task',
      project_id: 'Project',
      date_from: 'From',
      date_to: 'To',
      kind: 'Kind',
      half_day: 'Half day',
      note: 'Note',
      display_name: 'Name',
      birthday: 'Birthday',
      show_birthday: 'Birthday in team calendar',
      short_code: 'Short code',
      language: 'Language',
    },
  },
  notify: {
    hours: {
      rejected: 'Rejected: {{count}} entries in week {{week}}',
      rejected_one: 'Rejected: 1 entry in week {{week}}',
      approved: 'Approved: {{count}} entries in week {{week}}',
      approved_one: 'Approved: 1 entry in week {{week}}',
      l1: 'First approval given: {{count}} entries in week {{week}} wait for the final approval',
      l1_one: 'First approval given: 1 entry in week {{week}} waits for the final approval',
      waiting: '{{count}} entries in week {{week}} have been waiting for approval for {{days}} days',
      waiting_one: '1 entry in week {{week}} has been waiting for approval for {{days}} days',
    },
    missing: {
      days: 'No booking on {{count}} working days (latest {{last}})',
      days_one: 'No booking on {{last}}',
      week_due: 'Deadline for week {{week}} is today: {{count}} drafts are not submitted yet',
      week_due_one: 'Deadline for week {{week}} is today: 1 draft is not submitted yet',
      week_overdue: 'Deadline for week {{week}} ({{deadline}}) has passed: {{count}} drafts are not submitted yet',
      week_overdue_one: 'Deadline for week {{week}} ({{deadline}}) has passed: 1 draft is not submitted yet',
    },
    leave: {
      pending: '{{kind}} {{period}} is waiting for a decision',
      approved: '{{kind}} {{period}} approved',
      rejected: '{{kind}} {{period}} rejected',
      change_requested: 'Change requested for {{kind}} {{period}}: please adjust and submit again',
      cancelled: '{{kind}} {{period}} was cancelled',
      document_missing: '{{count}} absences without a document (sick note or certificate)',
      document_missing_one: '1 absence without a document (sick note or certificate)',
      overtime_reset: 'Your extra hours balance was reset by {{by}}: extra hours count from {{date}}. Balance before {{before}} h, now {{after}} h; removed: {{hours}} h. Your booked hours stay unchanged.',
    },
    approvals: {
      time: '{{count}} time entries wait for your approval (the oldest for {{days}} days)',
      time_one: '1 time entry waits for your approval ({{days}} days)',
      leave: '{{count}} absence requests wait for your decision',
      leave_one: '1 absence request waits for your decision',
    },
    period: {
      open: 'The month {{month}} has not been closed yet',
    },
    announcement: {
      new: 'New announcement: {{title}}',
      confirm: 'Please confirm: {{title}}',
    },
    system: {
      sync_error: 'Forma sync: errors in the last 24 hours',
      access_lost: '{{count}} projects: access to Forma lost',
      access_lost_one: '1 project: access to Forma lost',
      backup_old: 'The last backup is older than 8 days',
      job_errors: 'Nightly maintenance or check of {{date}}: {{count}} errors or warnings',
      job_warnings: 'Nightly maintenance or check of {{date}}: {{count}} warnings',
    },
    forma: {
      own_deleted: '{{ref}} {{title}} ({{project}}) was deleted in Forma: please check your {{hours}} h on it and rebook them',
      own_lost: 'Access to {{ref}} {{title}} ({{project}}) in Forma was lost: please check your {{hours}} h on it',
      team_lost: '{{count}} Forma tasks that were deleted or are no longer reachable still carry {{hours}} h of {{persons}} people',
    },
  },
  ann: {
    title: 'Announcements',
    subtitle: 'News from the company: dates, notices, instructions. Only people to whom management has given the right “Announcements” can publish.',
    tabCurrent: 'Current (as everybody sees it)',
    tabManage: 'Manage',
    emptyCurrent: 'No current announcements for you.',
    emptyManage: 'No announcements published yet. Use “New announcement” to create the first one.',
    types: {
      info: 'Information',
      event: 'Event',
      closure: 'Office closure',
      deadline: 'Deadline',
      safety: 'Instruction / safety',
      it: 'IT and system',
      policy: 'Policy changed',
    },
    typeHints: {
      info: 'General message to the staff.',
      event: 'Event or appointment (summer party, team day). Also appears under “Coming up”.',
      closure: 'Bridge day or company holidays. Also appears under “Coming up”.',
      deadline: 'Reminder with a date (submit hours, holiday planning). Also appears under “Coming up”.',
      safety: 'Instruction or safety notice. Confirmation of reading is preset (proof).',
      it: 'Maintenance, password, new function.',
      policy: 'A policy or document was changed. Point to the place where the company keeps it with a link.',
    },
    priorities: {
      normal: 'Normal',
      important: 'Important',
      critical: 'Urgent',
    },
    pinned: 'Pinned',
    pinnedHint: 'Stays at the top',
    new: 'New',
    needsConfirm: 'Confirmation open',
    more: 'Show more',
    less: 'Show less',
    openLink: 'Open',
    markRead: 'Mark as read',
    confirm: 'Noted',
    confirmed: 'Thank you, your confirmation is saved.',
    confirmedShort: 'Confirmed',
    newButton: 'New announcement',
    showArchived: 'Show archived',
    status: {
      active: 'Active',
      planned: 'Planned',
      expired: 'Expired',
      archived: 'Archived',
    },
    needsAck: 'with confirmation',
    readText: '{{read}} of {{reach}} read',
    ackText: '{{ack}} confirmed',
    audienceAll: 'All employees',
    locationOne: 'Location {{code}}',
    projectsN: '{{count}} projects',
    projectsN_one: '1 project',
    roleLead: 'Project leads',
    every: {
      weekly: 'weekly',
      monthly: 'monthly',
      yearly: 'yearly',
    },
    everyN: 'every {{count}} {{unit}}',
    units: {
      weeks: 'weeks',
      months: 'months',
      years: 'years',
    },
    recurring: 'recurring',
    untilShort: 'until {{date}}',
    fromShort: 'from {{date}}',
    eventShort: 'date {{date}}',
    duplicate: 'Duplicate',
    receipts: 'Read receipts',
    restored: 'Announcement restored',
    archivedToast: 'Announcement archived',
    published: 'Announcement published',
    form: {
      titleNew: 'New announcement',
      titleEdit: 'Edit announcement',
      publish: 'Publish',
      type: 'Type',
      headline: 'Headline',
      body: 'Text',
      priority: 'Importance',
      pin: 'Pin to the top',
      pinHint: 'Pinned announcements come before the others (after urgent ones).',
      startsOn: 'Visible from',
      endsOn: 'Visible until',
      endsOnHint: 'Empty: stays visible until you archive the announcement.',
      endsOnRepeat: 'With repetition, “Visible for (days)” sets the duration.',
      eventDate: 'Date of the event',
      eventTime: 'Time',
      repeat: 'Repeat',
      repeatHint: 'For recurring matters, e.g. monthly hours submission or yearly instruction. Every repetition counts anew for the read receipts.',
      freq: 'Rhythm',
      interval: 'Interval ({{unit}})',
      until: 'Repeat until',
      duration: 'Visible for (days)',
      lead: 'Show in advance (days)',
      leadHint: 'It appears this many days before the start.',
      audience: 'Audience',
      audienceAll: 'All employees',
      audienceAllHint: 'External people do not see it.',
      audienceHint: 'Everyone who belongs to at least one of the groups sees the announcement.',
      locations: 'Locations (holiday calendar of the person)',
      roles: 'Roles',
      projects: 'Project teams',
      addProject: 'Add project …',
      externalHint: 'External people only see announcements aimed at their role “External” or one of their projects.',
      needAudience: 'Please choose an audience.',
      ack: 'Ask for a confirmation of reading',
      ackHint: 'For instructions: everybody confirms with one click. You see who has not confirmed yet.',
      linkUrl: 'Link (optional)',
      linkHint: 'Company policies and documents stay where they are; the link to them is enough here.',
      linkLabel: 'Link label',
      newRevision: 'Ask readers again as a new version',
      newRevisionHint: 'The text was changed. Off: only a correction, read and confirmed versions stay valid.',
    },
    receiptsTitle: 'Read receipts',
    receiptsOccurrence: 'Status for the period from {{date}}',
    receiptsReach: 'People reached',
    receiptsRead: 'Read',
    receiptsAcked: 'Confirmed',
    receiptsPending: 'Not confirmed yet ({{count}})',
    receiptsAllDone: 'Everybody has confirmed.',
    receiptsPrivacy: 'The system shows names only for announcements that ask for a confirmation. For all others you only see the numbers.',
    archiveFilter: 'Show',
    archiveModes: {
      active: 'Current',
      all: 'Current and archive',
      only: 'Archive only',
    },
    receiptsRevision: 'Version {{revision}}',
  },
  home: {
    announcements: 'Announcements',
    manageAnnouncements: 'Manage',
    allAnnouncements: 'Show all',
    noAnnouncements: 'No announcements.',
    moreAnnouncements: '{{count}} more announcements',
    moreAnnouncements_one: '1 more announcement',
    standards: 'Company standards and templates',
    standardsHint: 'Policies, templates and layouts of the company (opens in a new tab)',
    upcoming: 'Coming up',
    upcomingEmpty: 'Nothing is due in the next days.',
    up: {
      requested: 'requested',
      birthday: 'Birthday: {{name}}',
      projectEnd: 'Project end: {{project}}',
      weekDeadline: 'Deadline to submit week {{week}}',
    },
    upKinds: {
      leave: 'Absence',
      holiday: 'Public holiday',
      event: 'Event',
      closure: 'Office closure',
      deadline: 'Deadline',
      birthday: 'Birthday',
      project_end: 'Project end',
      week_deadline: 'Submit hours',
    },
    myWork: 'My tasks',
    tiles: {
      absence: 'Absence',
      openCount: '{{count}} open',
      toCorrect: '{{count}} to correct',
      publish: 'Publish announcement',
    },
  },
  quick: {
    pin: 'Pin as favorite',
    unpin: 'Remove favorite',
    favorites: 'Favorites',
    sortLabel: 'Order of the tasks',
    sort: {
      manual: 'My own order',
      hours: 'Most hours first',
      project: 'By project',
    },
    pinnedAt: '{{ref}} added to the favorites',
    moveUp: 'Move up',
    moveDown: 'Move down',
    favoritesEmpty: 'No favorites yet. Use the star to pin tasks you book often.',
    favoritesFull: 'At most 30 favorites are possible. Please remove one first.',
    multi: 'Book several days',
    multiHint: 'The same task with the same hours and activity on several days, e.g. a week on site. You see every day before booking.',
    from: 'From',
    to: 'To',
    weekdays: 'Weekdays',
    hoursPerDay: 'Hours per day',
    daysCount: '{{count}} days',
    daysCount_one: '{{count}} day',
    daysNone: 'No day in the chosen period',
    daysTooMany: 'At most {{max}} days at once',
    checkDays: 'Check days',
    back: 'Back',
    bookN: 'Book {{count}} entries',
    bookN_one: 'Book {{count}} entry',
    bookNDraft: 'Create {{count}} draft entries',
    bookNDraft_one: 'Create {{count}} draft entry',
    reviewIntro: 'This shows what will happen before anything is booked. Days with a note (holiday, absence, already booked) are not ticked; tick them if you did work. Days that cannot be booked are grey.',
    selected: '{{count}} of {{total}} selected, {{hours}} h in total',
    doneMany: '{{count}} entries created',
    doneMany_one: '{{count}} entry created',
    partial: '{{ok}} created, {{failed}} not. The rest are listed with the reason.',
    unknownTask: 'Task not available',
    hint: {
      DUPLICATE: 'Task already booked that day',
      WEEKEND: 'Weekend',
      WEEKEND_DATE: 'Weekend',
      FREE_DAY: 'Day off',
      HOLIDAY: 'Public holiday',
      ABSENCE_DAY: 'Absence',
    },
    copyWeek: 'Copy a week',
    copyTitle: 'Copy a week',
    copyIntro: 'Copies the entries of an earlier week or of a template into week {{week}}. Everything is created as a draft; you check every line first and submit as usual.',
    source: 'Source',
    sourceLast: 'Last week (week {{week}})',
    sourceOther: 'Another week …',
    sourceTemplate: 'Template: {{name}}',
    otherWeek: 'A date in the week you want',
    emptySource: 'There is nothing to copy in this source.',
    saveTemplate: 'Save this week as a template',
    templateHint: 'The template remembers task, weekday, hours, activity and work location of the entries in week {{week}}. Only you can see it.',
    templateName: 'Template name',
    templateNamePlaceholder: 'e.g. Standard week',
    templateSave: 'Save template',
    templateSaved: 'Template “{{name}}” saved',
    templateDelete: 'Delete template',
    templateDeleted: 'Template deleted',
    templateLimit: 'At most 20 templates are possible. Please delete one first.',
    templateEmptyWeek: 'Week {{week}} has no entries yet that could be saved as a template.',
  },
  recurring: {
    title: 'Recurring',
    add: 'New',
    empty: 'Weekly appointments such as the jour fixe: choose "Repeat every week" in the booking dialog.',
    edit: 'Edit',
    editTitle: 'Edit recurring entry',
    pause: 'Pause',
    resume: 'Resume',
    paused: 'paused',
    pausedDone: 'Paused: no more drafts are created',
    resumedDone: 'Resumed',
    notBookable: 'task can no longer be booked',
    delete: 'Delete',
    deleteTitle: 'Delete recurring entry?',
    deleteText: 'No more drafts are created. Entries created already stay in your hours and can be changed or deleted there.',
    deleted: 'Recurring entry deleted',
    save: 'Save as recurring',
    saved: 'Recurring entry saved',
    savedBooked: 'Recurring entry saved, {{count}} drafts created this week',
    savedBooked_one: 'Recurring entry saved, {{count}} draft created this week',
    bookedNow: '{{count}} recurring entries created as drafts',
    bookedNow_one: '{{count}} recurring entry created as a draft',
    from: 'From',
    until: 'Until (optional)',
    untilHint: 'Empty: no end',
    untilShort: 'until {{date}}',
    hoursPerDay: 'Hours per day',
    toggle: 'Repeat every week',
    toggleHint: 'For example a weekly jour fixe: on the chosen weekdays of every week, as a draft.',
    toggleOnHint: 'The entries are created as drafts when you open the week (the current week also overnight).',
    everyWeek: 'every week',
    explain: 'Public holidays, company closures, full-day absences and days without target hours are left out. A deleted draft does not come back. As always, the week is submitted as a whole.',
  },
  templates: {
    title: 'Templates',
    new: 'New template',
    empty: 'No templates yet.',
    pick: 'Choose a template on the left or create one.',
    count: '{{count}} tasks',
    count_one: '{{count}} task',
    hint: 'Templates for manual tasks: create them once (e.g. 20 to 30 standard tasks) and take them over into a new project in one step under Tasks > "From template". The tasks are created as if by hand; titles the project has already are left out.',
    name: 'Name of the template',
    namePlaceholder: 'e.g. MEP standard',
    fromProject: 'Take over the tasks of a project',
    fromProjectHint: 'Adds the manual tasks of the project below',
    fromProjectDone: '{{count}} tasks added from the project',
    descriptionPlaceholder: 'Description (optional)',
    billingDefault: 'Default of the category',
    fixed: 'fixed for all bookings',
    duplicate: 'Title appears twice',
    addRow: 'Row',
    rowsHint: '{{count}} of at most 100 rows',
    removeRow: 'Remove row',
    deleteTitle: 'Delete template "{{name}}"?',
    deleteText: 'Tasks taken over from the template already stay in their projects.',
    deleted: 'Template deleted',
    fromTemplate: 'From template',
    applyTitle: 'Take tasks over from a template',
    applyHint: 'The chosen rows are created as manual tasks. Planned hours and billing type are taken over only by people who may set them in the project; otherwise the default of the category applies.',
    applyN: 'Create {{count}} tasks',
    applyN_one: 'Create {{count}} task',
    applied: '{{count}} tasks created',
    applied_one: '{{count}} task created',
    appliedSkipped: '{{count}} left out (title exists already or category inactive)',
    exists: 'in the project already',
    noneYet: 'There are no templates yet. The office creates them under Administration > Task templates.',
  },
  search: {
    open: 'Find a section (Ctrl+K)',
    placeholder: 'Find a section …',
    title: 'Find a section',
    inputPlaceholder: 'Name of a section, e.g. overtime, holidays, approvals',
    none: 'No section found.',
    hint: 'Searches only the sections and tabs you may open, not data. Arrow keys choose, Enter opens.',
  },
};

export default en;
